<template>
  <main class="site-page relative min-h-screen overflow-hidden bg-[var(--page)] text-[var(--text)] antialiased" :style="[
    themeStyle,
    {
      '--pointer-x': `${pointer.x}%`,
      '--pointer-y': `${pointer.y}%`
    }
  ]" @pointermove="handlePointerMove">
    <div class="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div class="ambient-grid absolute inset-0"></div>
      <div class="ambient-noise absolute inset-0"></div>
      <div class="pointer-glow absolute inset-0"></div>
      <div class="ambient-orb ambient-orb-one"></div>
      <div class="ambient-orb ambient-orb-two"></div>
    </div>

    <transition name="scroll-top">
      <button v-show="showScrollTop" type="button"
        class="fixed bottom-5 right-5 z-50 grid h-11 w-11 place-items-center rounded-full border border-[var(--border)] bg-[var(--glass-strong)] text-[var(--text-strong)] shadow-[var(--shadow-soft)] backdrop-blur-2xl transition duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]"
        aria-label="Voltar ao topo" title="Voltar ao topo" @click="scrollToTop">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
          class="h-5 w-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 15l6-6 6 6" />
        </svg>
      </button>
    </transition>

    <!-- HERO -->
    <section class="relative z-10">
      <div class="mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <router-link to="/templates"
          class="secondary-button inline-flex h-10 items-center gap-2 rounded-full px-4 text-xs font-semibold"
          data-reveal>
          <span aria-hidden="true">←</span>
          Todos os templates
        </router-link>

        <div class="mt-12 grid gap-14 lg:grid-cols-12 lg:items-center">
          <div class="lg:col-span-5" data-reveal>
            <div class="flex flex-wrap items-center gap-2">
              <span class="accent-pill rounded-full px-3 py-1.5 text-xs font-semibold">{{ solution.category }}</span>
              <span
                class="inline-flex items-center gap-2 rounded-full bg-[var(--text-strong)] px-3 py-1.5 text-xs font-semibold text-[var(--inverse)]">
                <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                Modelo demonstrativo
              </span>
            </div>

            <h1
              class="mt-7 text-5xl font-semibold tracking-[-0.06em] text-[var(--text-strong)] sm:text-6xl lg:text-7xl">
              {{ solution.heroTitle }}
            </h1>

            <p class="mt-6 max-w-2xl text-lg leading-8 text-[var(--muted)] sm:text-xl">
              {{ solution.heroDescription }}
            </p>

            <div class="mt-8 flex flex-col gap-3 sm:flex-row">
              <a :href="whatsHref" target="_blank" rel="noopener"
                class="primary-button inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold">
                Quero uma solução assim <span aria-hidden="true">→</span>
              </a>
              <a href="#como-funciona"
                class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold">Entender
                a solução</a>
            </div>

            <div class="mt-10 grid grid-cols-3 gap-3">
              <div v-for="metric in solution.metrics" :key="metric.label"
                class="metric-card rounded-[1.35rem] border border-[var(--border)] bg-[var(--glass)] p-4 backdrop-blur-xl">
                <p class="text-2xl font-semibold tracking-tight text-[var(--text-strong)] sm:text-3xl">{{ metric.value
                  }}</p>
                <p class="mt-1 text-xs leading-5 text-[var(--muted)] sm:text-sm">{{ metric.label }}</p>
              </div>
            </div>
          </div>

          <div class="lg:col-span-7" data-reveal>
            <div class="relative">
              <div class="accent-halo absolute inset-x-16 -bottom-6 h-28 rounded-full blur-3xl"></div>
              <div class="preview-frame relative rounded-[2.5rem] p-2.5 sm:p-3">
                <div class="overflow-hidden rounded-[2rem] bg-[#09090b] text-white">
                  <div class="flex items-center justify-between border-b border-white/10 px-5 py-4 sm:px-6">
                    <div class="flex items-center gap-3">
                      <div class="flex gap-1.5"><span class="h-2.5 w-2.5 rounded-full bg-white/20"></span><span
                          class="h-2.5 w-2.5 rounded-full bg-white/20"></span><span
                          class="h-2.5 w-2.5 rounded-full bg-white/20"></span></div><span
                        class="text-xs font-medium text-zinc-400">{{ solution.productName }}</span>
                    </div>
                    <span
                      class="rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold text-zinc-300 ring-1 ring-white/10">Live
                      preview</span>
                  </div>
                  <div class="min-h-[28rem] p-4 sm:p-6 lg:min-h-[31rem]">
                    <component :is="previewComponent" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chips-rail mt-14 flex flex-wrap items-center justify-center gap-2" data-reveal>
          <span v-for="chip in solution.heroChips" :key="chip"
            class="rounded-full border border-[var(--border)] bg-[var(--glass)] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--muted)] backdrop-blur-xl">{{
            chip }}</span>
        </div>
      </div>
    </section>

    <div class="relative z-10 mx-auto h-20 max-w-7xl px-5 sm:px-6 lg:px-8" aria-hidden="true"><span
        class="flow-line"></span><span class="flow-node"></span></div>

    <!-- CENÁRIO -->
    <section class="relative z-10 py-16 sm:py-20">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="grid gap-10 lg:grid-cols-12 lg:items-end" data-reveal>
          <div class="lg:col-span-7">
            <p class="accent-label">O cenário</p>
            <h2 class="section-title mt-4">{{ solution.problemTitle }}</h2>
          </div>
          <p class="text-lg leading-8 text-[var(--muted)] lg:col-span-5">{{ solution.problemDescription }}</p>
        </div>

        <div class="mt-14 grid gap-4 md:grid-cols-3 md:items-start">
          <article v-for="(pain, index) in solution.pains" :key="pain.title"
            :class="['pain-card rounded-[2rem] border border-[var(--border)] bg-[var(--card)] p-7 backdrop-blur-xl transition duration-500', index === 1 ? 'md:mt-8' : '']"
            data-reveal>
            <div class="accent-icon grid h-11 w-11 place-items-center rounded-2xl text-sm font-bold">0{{ index + 1 }}
            </div>
            <h3 class="mt-6 text-xl font-semibold tracking-tight text-[var(--text-strong)]">{{ pain.title }}</h3>
            <p class="mt-3 text-sm leading-6 text-[var(--muted)]">{{ pain.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <div class="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 lg:px-8" aria-hidden="true">
      <div class="curve-transition"><span class="curve-dot"></span></div>
    </div>

    <!-- COMO FUNCIONA -->
    <section id="como-funciona" class="relative z-10 py-14 sm:py-20">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div
          class="process-shell relative overflow-hidden rounded-[2.6rem] border border-[var(--border)] bg-[var(--glass)] p-5 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:p-8 lg:p-10"
          data-reveal>
          <div class="accent-process-glow absolute -right-28 -top-28 h-80 w-80 rounded-full blur-3xl"></div>
          <div class="relative grid gap-12 lg:grid-cols-[.78fr_1.22fr]">
            <div>
              <p class="accent-label">Como funciona</p>
              <h2 class="mt-4 text-4xl font-semibold tracking-[-0.05em] text-[var(--text-strong)] sm:text-5xl">{{
                solution.storyTitle }}</h2>
              <p class="mt-5 text-base leading-7 text-[var(--muted)]">{{ solution.storyDescription }}</p>

              <div class="mt-8 space-y-3">
                <button v-for="(step, index) in solution.storySteps" :key="step.title" type="button"
                  @click="activeStep = index" :aria-pressed="activeStep === index" :class="[
                    'w-full rounded-[1.5rem] border p-4 text-left transition duration-300',
                    activeStep === index ? 'border-transparent bg-[var(--text-strong)] text-[var(--inverse)] shadow-[var(--shadow-soft)]' : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text-strong)] hover:translate-x-1'
                  ]">
                  <div class="flex items-start gap-3"><span
                      :class="activeStep === index ? 'bg-[var(--inverse)] text-[var(--text-strong)]' : 'bg-[var(--chip)] text-[var(--muted)]'"
                      class="grid h-8 w-8 shrink-0 place-items-center rounded-xl text-xs font-bold">{{ index + 1
                      }}</span>
                    <div>
                      <p class="font-semibold">{{ step.title }}</p>
                      <p :class="activeStep === index ? 'text-current opacity-60' : 'text-[var(--muted)]'"
                        class="mt-1.5 text-sm leading-6">{{ step.description }}</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div class="story-panel relative overflow-hidden rounded-[2rem] bg-[#090a0c] p-6 text-white sm:p-8 lg:p-10">
              <div class="accent-dark-glow absolute -right-20 -top-20 h-64 w-64 rounded-full blur-3xl"></div>
              <div class="relative">
                <p class="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Etapa {{ activeStep + 1 }}</p>
                <h3 class="mt-4 max-w-2xl text-3xl font-semibold tracking-[-0.035em] sm:text-4xl">{{
                  activeStoryStep.title }}</h3>
                <p class="mt-4 max-w-2xl text-base leading-7 text-zinc-400 sm:text-lg">{{
                  activeStoryStep.longDescription }}</p>
                <div class="mt-9 grid gap-3 sm:grid-cols-3">
                  <div v-for="item in activeStoryStep.outcomes" :key="item"
                    class="rounded-2xl bg-white/[0.06] p-4 text-sm font-medium text-zinc-300 ring-1 ring-white/10">{{
                    item }}</div>
                </div>
                <div class="mt-9 rounded-[1.75rem] bg-white/[0.05] p-4 ring-1 ring-white/10 sm:p-5">
                  <div class="flex items-center justify-between gap-4">
                    <p class="text-sm font-semibold">{{ activeStoryStep.panelTitle }}</p><span
                      class="rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold text-zinc-400">Exemplo</span>
                  </div>
                  <div class="mt-5 grid grid-cols-3 gap-2">
                    <div v-for="n in 3" :key="n" class="rounded-xl bg-white/[0.06] p-3 ring-1 ring-white/10"><span
                        class="block h-2 rounded-full bg-white/20" :class="n === 2 ? 'w-2/3' : 'w-full'"></span><span
                        class="mt-3 block h-6 rounded-lg bg-white/10"></span></div>
                  </div>
                  <div class="mt-3 grid grid-cols-[1.4fr_.6fr] gap-2">
                    <div class="h-28 rounded-xl bg-white/[0.06] ring-1 ring-white/10"></div>
                    <div class="h-28 rounded-xl bg-white/[0.06] ring-1 ring-white/10"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- MÓDULOS -->
    <section class="relative z-10 py-20 sm:py-24">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="grid gap-10 lg:grid-cols-12 lg:items-end" data-reveal>
          <div class="lg:col-span-7">
            <p class="accent-label">Módulos</p>
            <h2 class="section-title mt-4">{{ solution.modulesTitle }}</h2>
          </div>
          <p class="text-lg leading-8 text-[var(--muted)] lg:col-span-5">{{ solution.modulesDescription }}</p>
        </div>
        <div class="mt-14 grid gap-4 md:grid-cols-2 xl:grid-cols-12 xl:items-start">
          <article v-for="(module, index) in solution.modules" :key="module.title"
            :class="['module-card rounded-[2rem] border border-[var(--border)] bg-[var(--card)] p-7 backdrop-blur-xl transition duration-500', moduleSpan(index)]"
            data-reveal>
            <div class="accent-icon grid h-12 w-12 place-items-center rounded-2xl text-lg font-semibold">{{
              module.symbol }}</div>
            <h3 class="mt-6 text-xl font-semibold tracking-tight text-[var(--text-strong)]">{{ module.title }}</h3>
            <p class="mt-3 text-sm leading-6 text-[var(--muted)]">{{ module.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- BENEFÍCIOS -->
    <section class="relative z-10 py-14 sm:py-20">
      <div class="mx-auto max-w-[88rem] px-3 sm:px-5 lg:px-7">
        <div
          class="dark-panel relative overflow-hidden rounded-[2.7rem] px-5 py-16 text-white shadow-[var(--shadow-large)] sm:px-8 sm:py-20 lg:px-12 xl:px-16">
          <div class="dark-panel-grid pointer-events-none absolute inset-0"></div>
          <div class="accent-dark-glow absolute -right-24 -top-28 h-96 w-96 rounded-full blur-3xl"></div>
          <div class="relative grid gap-10 lg:grid-cols-12">
            <div class="lg:col-span-7" data-reveal>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">O resultado</p>
              <h2 class="mt-5 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-5xl">{{ solution.benefitsTitle
                }}</h2>
              <div class="mt-9 grid gap-3 sm:grid-cols-2">
                <div v-for="benefit in solution.benefits" :key="benefit"
                  class="flex gap-3 rounded-2xl bg-white/[0.055] p-4 ring-1 ring-white/10"><span
                    class="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-white text-[10px] font-bold text-black">✓</span>
                  <p class="text-sm leading-6 text-zinc-300">{{ benefit }}</p>
                </div>
              </div>
            </div>
            <div class="lg:col-span-5" data-reveal>
              <div class="rounded-[2rem] bg-white/[0.055] p-7 ring-1 ring-white/10 backdrop-blur-xl">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Integrações</p>
                <h3 class="mt-4 text-3xl font-semibold tracking-[-0.035em]">Conecta com o que sua operação já usa.</h3>
                <p class="mt-4 text-sm leading-6 text-zinc-400">APIs, webhooks e serviços entram no mesmo fluxo sem
                  exigir que a empresa recomece do zero.</p>
                <div class="mt-7 flex flex-wrap gap-2"><span v-for="integration in solution.integrations"
                    :key="integration"
                    class="rounded-full bg-white/[0.07] px-4 py-2 text-sm font-semibold text-zinc-300 ring-1 ring-white/10">{{
                    integration }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PROCESSO -->
    <section class="relative z-10 py-20 sm:py-24">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="mx-auto max-w-3xl text-center" data-reveal>
          <p class="accent-label">Da ideia ao produto</p>
          <h2 class="section-title mx-auto mt-4">Construído em cima da sua operação.</h2>
          <p class="mt-5 text-lg leading-8 text-[var(--muted)]">O modelo serve como referência visual. Escopo, regras,
            módulos e integrações são definidos para o seu cenário.</p>
        </div>
        <div class="relative mt-14">
          <div class="process-line absolute left-[12.5%] right-[12.5%] top-6 hidden h-px lg:block"></div>
          <div class="grid gap-4 lg:grid-cols-4">
            <article v-for="(step, index) in process" :key="step.title"
              class="process-card relative rounded-[1.8rem] border border-[var(--border)] bg-[var(--card)] p-6 transition duration-500"
              data-reveal>
              <div
                class="relative z-10 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--text-strong)] text-sm font-bold text-[var(--inverse)] shadow-lg">
                {{ String(index + 1).padStart(2, '0') }}</div>
              <h3 class="mt-8 text-xl font-semibold text-[var(--text-strong)]">{{ step.title }}</h3>
              <p class="mt-3 text-sm leading-6 text-[var(--muted)]">{{ step.description }}</p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="relative z-10 py-14 sm:py-20">
      <div class="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
        <div
          class="faq-panel rounded-[2.5rem] border border-[var(--border)] bg-[var(--glass)] p-6 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:p-8 lg:p-10"
          data-reveal>
          <div class="grid gap-10 lg:grid-cols-12">
            <div class="lg:col-span-4">
              <p class="accent-label">Perguntas comuns</p>
              <h2 class="mt-4 text-4xl font-semibold tracking-[-0.05em] text-[var(--text-strong)]">Antes de começar.
              </h2>
            </div>
            <div class="divide-y divide-[var(--border)] lg:col-span-8">
              <details v-for="faq in solution.faq" :key="faq.question" class="group py-5">
                <summary
                  class="flex cursor-pointer list-none items-center justify-between gap-5 font-semibold text-[var(--text-strong)]">
                  <span>{{ faq.question }}</span><span
                    class="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[var(--border)] bg-[var(--surface)] text-lg transition duration-300 group-open:rotate-45 group-open:bg-[var(--text-strong)] group-open:text-[var(--inverse)]">+</span>
                </summary>
                <p class="max-w-3xl pb-2 pt-4 text-sm leading-7 text-[var(--muted)]">{{ faq.answer }}</p>
              </details>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="relative z-10 pb-24 pt-16 sm:pb-28 sm:pt-20">
      <div class="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
        <div
          class="cta-panel relative overflow-hidden rounded-[2.7rem] border border-[var(--border)] px-6 py-12 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:px-9 sm:py-14 lg:px-12"
          data-reveal>
          <div class="accent-halo absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"></div>
          <div class="relative grid gap-10 lg:grid-cols-12 lg:items-center">
            <div class="lg:col-span-8">
              <p class="accent-label">Próxima solução</p>
              <h2
                class="mt-4 max-w-3xl text-4xl font-semibold tracking-[-0.05em] text-[var(--text-strong)] sm:text-5xl">
                {{ solution.ctaTitle }}</h2>
              <p class="mt-5 max-w-2xl text-lg leading-8 text-[var(--muted)]">{{ solution.ctaDescription }}</p>
            </div>
            <div class="flex flex-col gap-3 lg:col-span-4"><a :href="whatsHref" target="_blank" rel="noopener"
                class="primary-button inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold">Conversar
                sobre este modelo <span aria-hidden="true">→</span></a><router-link to="/templates"
                class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold">Ver
                outras soluções</router-link></div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, defineComponent, h, onBeforeUnmount, onMounted, reactive, ref } from 'vue'

const props = defineProps({
  slug: { type: String, required: true }
})

const activeStep = ref(0)
const showScrollTop = ref(false)
const pointer = reactive({ x: 50, y: 20 })
let observer = null

const solutions = {
  'dashboard-financeiro': {
    category: 'Financeiro',
    productName: 'Clarity Finance',
    heroTitle: 'Seu financeiro explicado em uma única tela.',
    heroDescription: 'Um painel financeiro sob medida para transformar movimentações, metas e indicadores em decisões simples de acompanhar todos os dias.',
    accent: '#2563eb', accentSoft: '#dbeafe', preview: 'finance',
    metrics: [{ value: '1 visão', label: 'para toda a operação' }, { value: '24/7', label: 'indicadores atualizados' }, { value: '-manual', label: 'menos planilhas paralelas' }],
    heroChips: ['Fluxo de caixa', 'DRE', 'Metas', 'Open Finance', 'Cobranças', 'Relatórios'],
    problemTitle: 'Quando os números estão espalhados, a decisão chega atrasada.',
    problemDescription: 'A proposta é reunir dados financeiros e operacionais em um único ambiente, com leitura rápida e profundidade quando for necessário investigar.',
    pains: [
      { title: 'Visão fragmentada', description: 'Saldos, despesas e cobranças vivem em ferramentas diferentes e exigem conferência manual.' },
      { title: 'Pouca previsibilidade', description: 'Fica difícil enxergar tendência de caixa, margem e compromissos futuros com antecedência.' },
      { title: 'Relatórios demorados', description: 'A equipe gasta tempo organizando informação antes mesmo de começar a analisar.' }
    ],
    storyTitle: 'Do movimento bruto à leitura gerencial.',
    storyDescription: 'A solução organiza o ciclo financeiro em etapas claras para reduzir trabalho operacional e aumentar a qualidade da informação.',
    storySteps: [
      { title: 'Centralizar dados', description: 'Contas, lançamentos e integrações no mesmo fluxo.', longDescription: 'Conectamos fontes de dados e cadastros para que o financeiro tenha uma base única, consistente e pronta para conciliação.', outcomes: ['Contas consolidadas', 'Importação automática', 'Histórico central'], panelTitle: 'Visão consolidada' },
      { title: 'Aplicar regras', description: 'Categorias, centros e automações organizam a operação.', longDescription: 'Regras automáticas reduzem classificação manual e ajudam a manter a informação padronizada ao longo do tempo.', outcomes: ['Categorização', 'Centros de custo', 'Alertas e rotinas'], panelTitle: 'Motor de regras' },
      { title: 'Acompanhar indicadores', description: 'Painéis transformam dados em leitura rápida.', longDescription: 'Dashboards e relatórios mostram tendências, desvios e resultados sem exigir que a gestão fique montando planilhas.', outcomes: ['Fluxo de caixa', 'DRE gerencial', 'Metas e projeções'], panelTitle: 'Indicadores executivos' }
    ],
    modulesTitle: 'Tudo que um financeiro moderno pode reunir.',
    modulesDescription: 'Os módulos podem entrar por etapas, começando pelo que mais reduz esforço ou melhora a visibilidade do negócio.',
    modules: [
      { symbol: 'R$', title: 'Fluxo de caixa', description: 'Entradas, saídas, saldos e projeções em uma visão objetiva.' },
      { symbol: '%', title: 'Indicadores', description: 'Margem, resultado, ticket, recorrência e metas acompanhadas em tempo real.' },
      { symbol: '↔', title: 'Conciliação', description: 'Comparação entre movimentações, cobranças e registros internos.' },
      { symbol: '◎', title: 'Open Finance', description: 'Conexão autorizada para saldos e transações bancárias quando aplicável.' },
      { symbol: '!', title: 'Cobranças', description: 'Vencimentos, inadimplência, lembretes e régua de comunicação.' },
      { symbol: '↗', title: 'Relatórios', description: 'Visões gerenciais e exportações pensadas para cada perfil de decisão.' }
    ],
    benefitsTitle: 'Menos esforço para fechar números. Mais tempo para decidir.',
    benefits: ['Visão financeira consistente para gestão e operação.', 'Redução de planilhas e conferências repetitivas.', 'Mais previsibilidade de caixa e compromissos.', 'Relatórios que acompanham o formato do negócio.'],
    integrations: ['Open Finance', 'ERP', 'Gateway de pagamento', 'WhatsApp', 'E-mail', 'Planilhas'],
    faq: [
      { question: 'Precisa substituir meu sistema atual?', answer: 'Não necessariamente. A solução pode funcionar como camada de gestão e integração, conectando sistemas já utilizados pela empresa.' },
      { question: 'Dá para começar apenas com o dashboard?', answer: 'Sim. É possível começar com indicadores e evoluir para automações, conciliação e integrações em etapas.' },
      { question: 'Os indicadores podem seguir minhas regras?', answer: 'Sim. Categorias, fórmulas, metas e visões são definidas conforme a lógica financeira da operação.' }
    ],
    ctaTitle: 'Seu financeiro pode deixar de ser um quebra-cabeça.',
    ctaDescription: 'Usamos este modelo como ponto de partida e desenhamos módulos, integrações e indicadores em cima do seu processo.'
  },

  'crm-pipeline': {
    category: 'Comercial', productName: 'Flow CRM',
    heroTitle: 'Um comercial que sabe exatamente o próximo passo.',
    heroDescription: 'CRM sob medida para organizar leads, oportunidades, propostas e follow-ups sem transformar a rotina comercial em burocracia.',
    accent: '#7c3aed', accentSoft: '#ede9fe', preview: 'crm',
    metrics: [{ value: '1 funil', label: 'com toda a operação' }, { value: '+visão', label: 'sobre cada oportunidade' }, { value: '-perdas', label: 'por falta de follow-up' }],
    heroChips: ['Leads', 'Pipeline', 'Propostas', 'Follow-up', 'WhatsApp', 'Relatórios'],
    problemTitle: 'O lead não deveria depender da memória de quem está vendendo.',
    problemDescription: 'Uma operação comercial saudável precisa mostrar quem entrou, em que etapa está, qual o próximo contato e quanto existe de oportunidade aberta.',
    pains: [
      { title: 'Leads dispersos', description: 'Contatos chegam por diferentes canais e acabam sem responsável ou histórico confiável.' },
      { title: 'Follow-up irregular', description: 'Oportunidades esfriam porque a próxima ação não está clara ou não gera lembrete.' },
      { title: 'Previsão fraca', description: 'A gestão não consegue enxergar valor do pipeline, conversão e gargalos com segurança.' }
    ],
    storyTitle: 'Da entrada do lead ao fechamento, sem perder contexto.',
    storyDescription: 'Cada etapa do funil pode refletir seu processo real, com regras, responsáveis e automações específicas.',
    storySteps: [
      { title: 'Capturar e organizar', description: 'Leads entram com origem e contexto.', longDescription: 'Formulários, WhatsApp, indicações e integrações alimentam o funil com dados padronizados desde o primeiro contato.', outcomes: ['Origem do lead', 'Responsável', 'Dados centralizados'], panelTitle: 'Entrada de oportunidades' },
      { title: 'Conduzir a negociação', description: 'Etapas e atividades deixam o próximo passo visível.', longDescription: 'O time acompanha reuniões, propostas, tarefas e histórico sem depender de anotações espalhadas.', outcomes: ['Kanban comercial', 'Agenda de ações', 'Histórico completo'], panelTitle: 'Pipeline operacional' },
      { title: 'Medir e melhorar', description: 'Conversão e ciclo mostram onde o funil trava.', longDescription: 'Indicadores revelam taxa de conversão, tempo por etapa, origem dos melhores leads e previsão de fechamento.', outcomes: ['Conversão', 'Forecast', 'Performance por origem'], panelTitle: 'Visão gerencial' }
    ],
    modulesTitle: 'Um CRM moldado ao seu jeito de vender.',
    modulesDescription: 'O sistema pode ser enxuto para uma equipe pequena ou evoluir para uma operação comercial completa.',
    modules: [
      { symbol: '◎', title: 'Leads e contatos', description: 'Cadastro, origem, tags, histórico e dados importantes em um só lugar.' },
      { symbol: '→', title: 'Pipeline', description: 'Etapas configuráveis para refletir o processo comercial real.' },
      { symbol: '✓', title: 'Atividades', description: 'Tarefas, reuniões, follow-ups e lembretes vinculados à oportunidade.' },
      { symbol: '$', title: 'Propostas', description: 'Valores, versões, status e documentos ligados à negociação.' },
      { symbol: '✦', title: 'Automação', description: 'Distribuição de leads, alertas e ações disparadas por mudança de etapa.' },
      { symbol: '↗', title: 'Indicadores', description: 'Conversão, ciclo médio, ticket e previsão de receita.' }
    ],
    benefitsTitle: 'Mais consistência no processo. Menos oportunidade esquecida.',
    benefits: ['Funil visível para equipe e gestão.', 'Próximas ações claras para cada oportunidade.', 'Histórico centralizado desde o primeiro contato.', 'Indicadores alinhados ao processo comercial real.'],
    integrations: ['WhatsApp', 'E-mail', 'Formulários', 'ERP', 'Calendário', 'Assinatura digital'],
    faq: [
      { question: 'O pipeline pode ter minhas próprias etapas?', answer: 'Sim. Etapas, campos, permissões e regras podem seguir exatamente o processo comercial da empresa.' },
      { question: 'Consigo integrar WhatsApp?', answer: 'Sim, quando o cenário e a API utilizada permitirem. Conversas, notificações e eventos podem entrar no fluxo.' },
      { question: 'Dá para migrar dados de outro CRM?', answer: 'Sim. A viabilidade depende do formato de exportação ou API do sistema atual, mas a migração pode fazer parte do projeto.' }
    ],
    ctaTitle: 'Seu pipeline pode ficar tão claro quanto deveria ser.',
    ctaDescription: 'Adaptamos esta experiência ao seu ciclo de venda, equipe, canais e regras comerciais.'
  },

  'central-atendimento-whatsapp': {
    category: 'Atendimento', productName: 'Omni Inbox',
    heroTitle: 'Atendimento organizado, mesmo quando o volume cresce.',
    heroDescription: 'Uma central de conversas para WhatsApp e outros canais, com filas, responsáveis, histórico e automações em uma interface única.',
    accent: '#059669', accentSoft: '#d1fae5', preview: 'inbox',
    metrics: [{ value: '1 inbox', label: 'para todos os canais' }, { value: '+SLA', label: 'mais controle de resposta' }, { value: '360°', label: 'histórico do cliente' }],
    heroChips: ['WhatsApp', 'Filas', 'Times', 'SLA', 'Templates', 'Histórico'],
    problemTitle: 'Atendimento não pode virar uma sequência de conversas sem contexto.',
    problemDescription: 'Quando canais, responsáveis e histórico ficam separados, o cliente repete informações e a equipe perde velocidade.',
    pains: [
      { title: 'Conversas sem dono', description: 'Mensagens chegam, mas não existe uma distribuição clara entre pessoas e equipes.' },
      { title: 'Histórico quebrado', description: 'Informações importantes ficam presas em aparelhos, contas ou anotações paralelas.' },
      { title: 'Sem visão de SLA', description: 'A gestão não sabe quem está esperando, há quanto tempo ou onde o atendimento trava.' }
    ],
    storyTitle: 'Uma operação de atendimento com contexto do início ao fim.',
    storyDescription: 'A central organiza entrada, triagem, conversa e resolução mantendo histórico e indicadores.',
    storySteps: [
      { title: 'Receber', description: 'Mensagens entram em uma caixa única.', longDescription: 'Canais integrados alimentam uma inbox central, preservando identificação do cliente e origem da conversa.', outcomes: ['Inbox unificada', 'Origem identificada', 'Histórico por contato'], panelTitle: 'Entrada de conversas' },
      { title: 'Distribuir', description: 'Filas e regras direcionam cada atendimento.', longDescription: 'A conversa pode ser encaminhada por equipe, assunto, horário ou disponibilidade, reduzindo repasses manuais.', outcomes: ['Filas', 'Responsáveis', 'Prioridades'], panelTitle: 'Roteamento inteligente' },
      { title: 'Resolver e medir', description: 'Status e indicadores acompanham a operação.', longDescription: 'Fechamento, tags, tempo de resposta e motivos de contato ajudam a equipe a melhorar atendimento e capacidade.', outcomes: ['SLA', 'Motivos', 'Qualidade'], panelTitle: 'Gestão de atendimento' }
    ],
    modulesTitle: 'Do primeiro “olá” à resolução.',
    modulesDescription: 'A central pode começar com uma inbox simples e evoluir para automações, bots e integrações operacionais.',
    modules: [
      { symbol: '◌', title: 'Inbox omnichannel', description: 'Conversas organizadas em uma interface única para a equipe.' },
      { symbol: '⇄', title: 'Filas e distribuição', description: 'Regras de direcionamento entre times, pessoas e prioridades.' },
      { symbol: '⏱', title: 'SLA', description: 'Tempo de espera, primeira resposta e resolução acompanhados em painel.' },
      { symbol: '☰', title: 'Templates', description: 'Mensagens padronizadas para agilizar respostas frequentes.' },
      { symbol: '✦', title: 'Automação', description: 'Triagem, alertas, lembretes e ações vinculadas ao atendimento.' },
      { symbol: '↗', title: 'Relatórios', description: 'Volume, motivos, tempo e performance por equipe ou canal.' }
    ],
    benefitsTitle: 'Mais contexto para a equipe. Menos atrito para o cliente.',
    benefits: ['Histórico centralizado por cliente.', 'Distribuição clara de responsabilidades.', 'Mais controle sobre tempos de atendimento.', 'Base pronta para automações e bots.'],
    integrations: ['WhatsApp Cloud API', 'CRM', 'ERP', 'E-mail', 'IA', 'Webhooks'],
    faq: [
      { question: 'Precisa ser só WhatsApp?', answer: 'Não. O modelo pode ser pensado para outros canais dependendo das APIs disponíveis e da operação.' },
      { question: 'Pode ter vários atendentes?', answer: 'Sim. Filas, equipes, responsáveis e permissões podem ser configurados conforme a estrutura do atendimento.' },
      { question: 'Dá para incluir bot ou IA?', answer: 'Sim. Automação e IA podem atuar na triagem, respostas simples e apoio ao atendente, respeitando regras definidas.' }
    ],
    ctaTitle: 'Seu atendimento pode crescer sem virar caos.',
    ctaDescription: 'Desenhamos a central em cima dos seus canais, equipe, horários, SLAs e integrações.'
  },

  'agente-ia': {
    category: 'Inteligência artificial', productName: 'Nexa Agent',
    heroTitle: 'Uma IA que conhece sua operação e sabe onde agir.',
    heroDescription: 'Agentes conectados a dados, documentos e ferramentas para responder, analisar e executar tarefas dentro de fluxos controlados.',
    accent: '#4f46e5', accentSoft: '#e0e7ff', preview: 'ai',
    metrics: [{ value: 'Contexto', label: 'sobre dados do negócio' }, { value: 'Ações', label: 'com ferramentas conectadas' }, { value: '24/7', label: 'apoio operacional' }],
    heroChips: ['RAG', 'Ferramentas', 'Documentos', 'APIs', 'Automação', 'Logs'],
    problemTitle: 'IA útil não é só conversar. É entender contexto e participar do processo.',
    problemDescription: 'O agente ganha valor quando consegue consultar fontes confiáveis, seguir regras e executar ações que realmente economizam tempo da equipe.',
    pains: [
      { title: 'Conhecimento espalhado', description: 'Documentos, sistemas e informações operacionais ficam em fontes que a equipe precisa consultar manualmente.' },
      { title: 'Tarefas repetitivas', description: 'Pessoas gastam tempo buscando dados, resumindo informações e repetindo ações previsíveis.' },
      { title: 'IA sem governança', description: 'Sem contexto, permissões e logs, um chatbot genérico não serve para processos críticos.' }
    ],
    storyTitle: 'Contexto, decisão e ação no mesmo fluxo.',
    storyDescription: 'O agente pode ser desenhado para apoiar pessoas ou executar rotinas específicas com limites claros.',
    storySteps: [
      { title: 'Entender', description: 'Busca contexto em fontes autorizadas.', longDescription: 'Documentos, banco de dados e APIs formam a base de contexto para respostas mais aderentes ao negócio.', outcomes: ['Documentos', 'Dados estruturados', 'Memória de contexto'], panelTitle: 'Base de conhecimento' },
      { title: 'Raciocinar', description: 'Aplica regras e escolhe ferramentas.', longDescription: 'O agente interpreta intenção, respeita regras do processo e decide quais fontes ou ferramentas precisa utilizar.', outcomes: ['Regras', 'Permissões', 'Ferramentas'], panelTitle: 'Orquestração' },
      { title: 'Executar', description: 'Realiza ações e registra o resultado.', longDescription: 'Com autorização, o agente pode criar tarefas, consultar sistemas, preparar mensagens e acionar integrações com rastreabilidade.', outcomes: ['Ações', 'Logs', 'Aprovações'], panelTitle: 'Execução assistida' }
    ],
    modulesTitle: 'IA aplicada ao trabalho real.',
    modulesDescription: 'O agente pode começar como copiloto de consulta e evoluir para execução assistida ou automações mais completas.',
    modules: [
      { symbol: '✦', title: 'Chat contextual', description: 'Conversa com acesso controlado ao contexto da empresa.' },
      { symbol: '▤', title: 'RAG e documentos', description: 'Busca em políticas, contratos, manuais e bases internas.' },
      { symbol: '⚙', title: 'Ferramentas', description: 'Funções conectadas a sistemas e APIs específicas do negócio.' },
      { symbol: '✓', title: 'Aprovações', description: 'Ações críticas podem exigir confirmação humana antes da execução.' },
      { symbol: '↺', title: 'Workflows', description: 'Sequências de ações para tarefas repetitivas ou eventos operacionais.' },
      { symbol: '◎', title: 'Observabilidade', description: 'Logs, contexto usado e resultados disponíveis para auditoria.' }
    ],
    benefitsTitle: 'Menos busca manual. Mais capacidade para executar.',
    benefits: ['Acesso rápido a conhecimento interno.', 'Automação de tarefas de baixa complexidade.', 'Apoio à equipe sem perder governança.', 'Integração com ferramentas que já fazem parte da operação.'],
    integrations: ['Banco de dados', 'CRM', 'Financeiro', 'WhatsApp', 'Google Drive', 'APIs internas'],
    faq: [
      { question: 'O agente pode acessar dados privados?', answer: 'Pode, desde que a arquitetura e as permissões sejam desenhadas para isso. O acesso deve ser controlado e restrito ao necessário.' },
      { question: 'Ele pode executar ações sozinho?', answer: 'Dependendo do risco, ações podem ser automáticas ou exigir confirmação humana. Isso é definido por ferramenta e fluxo.' },
      { question: 'É possível usar documentos da empresa?', answer: 'Sim. Documentos podem ser indexados ou consultados por integrações apropriadas para servir de contexto ao agente.' }
    ],
    ctaTitle: 'Seu agente pode ser parte da operação, não só uma janela de chat.',
    ctaDescription: 'Mapeamos tarefas, fontes de contexto, ferramentas e limites para construir uma IA realmente útil ao seu processo.'
  },

  'backoffice-operacional': {
    category: 'Gestão', productName: 'Ops Console',
    heroTitle: 'A operação inteira organizada por trás da experiência.',
    heroDescription: 'Backoffice sob medida para concentrar cadastros, permissões, processos, tarefas e controles internos em um ambiente simples de operar.',
    accent: '#0f766e', accentSoft: '#ccfbf1', preview: 'backoffice',
    metrics: [{ value: '1 painel', label: 'para toda a equipe' }, { value: '+controle', label: 'sobre processos internos' }, { value: '-retrabalho', label: 'em rotinas operacionais' }],
    heroChips: ['Usuários', 'Permissões', 'Cadastros', 'Processos', 'Auditoria', 'Relatórios'],
    problemTitle: 'O cliente vê a ponta. O backoffice sustenta todo o resto.',
    problemDescription: 'Operações crescem melhor quando tarefas, cadastros e regras internas deixam de depender de planilhas, mensagens e conhecimento informal.',
    pains: [
      { title: 'Processos manuais', description: 'Rotinas críticas dependem de copiar dados, conferir planilhas ou avisar pessoas manualmente.' },
      { title: 'Pouca rastreabilidade', description: 'Fica difícil saber quem alterou algo, quando uma etapa foi concluída ou onde ocorreu um erro.' },
      { title: 'Permissões frágeis', description: 'Acesso e responsabilidade não acompanham a estrutura real das equipes.' }
    ],
    storyTitle: 'Uma camada interna para a operação funcionar com consistência.',
    storyDescription: 'O backoffice reúne ferramentas administrativas sem expor complexidade para quem está na ponta.',
    storySteps: [
      { title: 'Organizar cadastros', description: 'Dados principais ganham uma estrutura única.', longDescription: 'Clientes, usuários, produtos ou entidades do negócio ficam centralizados com validações e histórico.', outcomes: ['Cadastros únicos', 'Validações', 'Busca rápida'], panelTitle: 'Dados operacionais' },
      { title: 'Controlar processos', description: 'Status e responsáveis deixam cada etapa visível.', longDescription: 'Fluxos operacionais passam a ter estados, responsáveis, prazos e regras que reduzem improviso.', outcomes: ['Status', 'Responsáveis', 'Prazos'], panelTitle: 'Gestão de processos' },
      { title: 'Auditar e evoluir', description: 'Eventos e métricas mostram o que acontece por trás.', longDescription: 'Logs, relatórios e indicadores ajudam a diagnosticar gargalos e melhorar a operação com segurança.', outcomes: ['Auditoria', 'Indicadores', 'Relatórios'], panelTitle: 'Controle gerencial' }
    ],
    modulesTitle: 'As ferramentas internas que sua equipe realmente precisa.',
    modulesDescription: 'Em vez de adaptar o processo a um sistema genérico, os módulos acompanham sua lógica operacional.',
    modules: [
      { symbol: 'U', title: 'Usuários', description: 'Perfis, grupos, status e estrutura de acesso.' },
      { symbol: '🔒', title: 'Permissões', description: 'Controle granular por função, área ou tipo de operação.' },
      { symbol: '▤', title: 'Cadastros', description: 'Dados centrais organizados e validados em uma única base.' },
      { symbol: '→', title: 'Workflows', description: 'Etapas, responsáveis, prazos e regras do processo interno.' },
      { symbol: '◎', title: 'Auditoria', description: 'Registro de eventos e alterações importantes para rastreabilidade.' },
      { symbol: '↗', title: 'Relatórios', description: 'Visões operacionais e gerenciais para acompanhar o que importa.' }
    ],
    benefitsTitle: 'Uma operação mais previsível por trás de cada entrega.',
    benefits: ['Menos dependência de controles paralelos.', 'Rastreabilidade de ações e mudanças.', 'Permissões alinhadas à estrutura da equipe.', 'Base pronta para automações e integrações.'],
    integrations: ['ERP', 'CRM', 'Financeiro', 'E-mail', 'WhatsApp', 'APIs internas'],
    faq: [
      { question: 'Pode substituir planilhas internas?', answer: 'Sim, quando os fluxos e regras puderem ser estruturados em módulos e processos dentro do sistema.' },
      { question: 'Consigo ter perfis diferentes de acesso?', answer: 'Sim. Permissões podem ser configuradas por perfil, equipe, módulo ou ação.' },
      { question: 'Dá para integrar com sistemas legados?', answer: 'Quando existe API, banco acessível ou outro mecanismo seguro de integração, o backoffice pode conversar com sistemas existentes.' }
    ],
    ctaTitle: 'Sua equipe pode operar com menos improviso.',
    ctaDescription: 'Transformamos rotinas internas em uma ferramenta clara, segura e preparada para evoluir com o negócio.'
  },

  'agenda-consultorio': {
    category: 'Gestão', productName: 'Care Schedule',
    heroTitle: 'Agenda, clientes e rotina do consultório no mesmo fluxo.',
    heroDescription: 'Uma experiência de agenda construída para reduzir conflitos, organizar atendimentos e dar mais contexto para recepção e profissionais.',
    accent: '#db2777', accentSoft: '#fce7f3', preview: 'calendar',
    metrics: [{ value: '1 agenda', label: 'para toda a equipe' }, { value: '-faltas', label: 'com lembretes e confirmações' }, { value: '+fluxo', label: 'na rotina diária' }],
    heroChips: ['Agenda', 'Clientes', 'Profissionais', 'Lembretes', 'Status', 'Histórico'],
    problemTitle: 'Agenda não é só horário. É o centro da rotina de atendimento.',
    problemDescription: 'Quando marcação, confirmação, histórico e comunicação ficam separados, a equipe perde tempo e aumenta a chance de falhas.',
    pains: [
      { title: 'Conflito de horários', description: 'Disponibilidade e regras de agenda não ficam claras para toda a equipe.' },
      { title: 'Faltas e remarcações', description: 'Confirmações dependem de contatos manuais e não existe uma visão organizada das mudanças.' },
      { title: 'Pouco contexto', description: 'Recepção e profissionais precisam buscar informações em diferentes lugares antes de atender.' }
    ],
    storyTitle: 'Da marcação ao pós-atendimento em uma jornada contínua.',
    storyDescription: 'A agenda pode refletir especialidades, profissionais, salas, tipos de atendimento e regras específicas do consultório.',
    storySteps: [
      { title: 'Organizar disponibilidade', description: 'Horários seguem regras por profissional e serviço.', longDescription: 'Janelas de atendimento, bloqueios, duração e recursos podem ser configurados para reduzir conflitos.', outcomes: ['Disponibilidade', 'Bloqueios', 'Recursos'], panelTitle: 'Agenda inteligente' },
      { title: 'Confirmar e lembrar', description: 'Comunicação reduz faltas e retrabalho.', longDescription: 'Lembretes e confirmações podem seguir fluxos automáticos por WhatsApp ou outros canais integrados.', outcomes: ['Confirmação', 'Lembretes', 'Remarcação'], panelTitle: 'Comunicação com cliente' },
      { title: 'Acompanhar a rotina', description: 'Status mostra o andamento do dia.', longDescription: 'Chegada, atendimento, conclusão e pendências ficam visíveis para quem precisa operar a agenda.', outcomes: ['Check-in', 'Status', 'Histórico'], panelTitle: 'Operação diária' }
    ],
    modulesTitle: 'Uma agenda que entende como o consultório funciona.',
    modulesDescription: 'A solução pode ir do agendamento simples até uma plataforma completa de relacionamento e operação.',
    modules: [
      { symbol: '◫', title: 'Calendário', description: 'Visões diária, semanal e por profissional com filtros rápidos.' },
      { symbol: 'P', title: 'Profissionais', description: 'Disponibilidade, horários, especialidades e regras individuais.' },
      { symbol: 'C', title: 'Clientes', description: 'Cadastro, histórico de atendimentos e informações relevantes.' },
      { symbol: '✓', title: 'Confirmações', description: 'Status de confirmação, cancelamento e remarcação centralizados.' },
      { symbol: '✉', title: 'Lembretes', description: 'Mensagens automáticas antes e depois do atendimento.' },
      { symbol: '↗', title: 'Indicadores', description: 'Ocupação, faltas, remarcações e volume por profissional.' }
    ],
    benefitsTitle: 'Uma rotina mais leve para equipe e clientes.',
    benefits: ['Menos conflito e retrabalho na agenda.', 'Confirmações e lembretes mais consistentes.', 'Visão diária clara para recepção e profissionais.', 'Histórico centralizado por cliente.'],
    integrations: ['WhatsApp', 'Google Calendar', 'Financeiro', 'E-mail', 'CRM', 'Prontuário/API'],
    faq: [
      { question: 'Pode ter vários profissionais?', answer: 'Sim. Cada profissional pode ter disponibilidade, tipos de atendimento e regras próprias.' },
      { question: 'Dá para enviar confirmação pelo WhatsApp?', answer: 'Sim, quando integrado à API adequada. Lembretes, confirmações e remarcações podem fazer parte do fluxo.' },
      { question: 'Pode existir agenda por sala ou equipamento?', answer: 'Sim. Recursos compartilhados podem ser considerados nas regras de disponibilidade.' }
    ],
    ctaTitle: 'Sua agenda pode trabalhar junto com a equipe.',
    ctaDescription: 'Adaptamos este modelo aos profissionais, serviços, horários, confirmações e integrações do seu consultório.'
  },

  'portal-cliente': {
    category: 'Atendimento', productName: 'Client Space',
    heroTitle: 'Um espaço onde o cliente encontra tudo sem precisar perguntar.',
    heroDescription: 'Portal autenticado para centralizar documentos, solicitações, pagamentos, andamento de serviços e comunicação com a empresa.',
    accent: '#0284c7', accentSoft: '#e0f2fe', preview: 'portal',
    metrics: [{ value: '24/7', label: 'acesso do cliente' }, { value: '-tickets', label: 'sobre dúvidas simples' }, { value: '1 lugar', label: 'para toda a jornada' }],
    heroChips: ['Login', 'Documentos', 'Solicitações', 'Pagamentos', 'Status', 'Histórico'],
    problemTitle: 'Boa experiência também é deixar a informação fácil de encontrar.',
    problemDescription: 'Quando o cliente depende do atendimento para cada documento, status ou segunda via, a operação acumula solicitações que poderiam ser resolvidas sozinhas.',
    pains: [
      { title: 'Pedidos repetitivos', description: 'Equipe responde constantemente sobre status, documentos, cobranças e informações já disponíveis internamente.' },
      { title: 'Experiência fragmentada', description: 'Arquivos e comunicações chegam por diferentes canais sem um histórico claro para o cliente.' },
      { title: 'Pouca transparência', description: 'O cliente não consegue acompanhar andamento e próximos passos de forma autônoma.' }
    ],
    storyTitle: 'Autonomia para o cliente sem perder controle da operação.',
    storyDescription: 'O portal entrega uma camada de autosserviço conectada aos dados e processos internos da empresa.',
    storySteps: [
      { title: 'Acessar com segurança', description: 'Cada cliente vê apenas o que pertence à sua conta.', longDescription: 'Autenticação e permissões garantem acesso restrito a documentos, solicitações e informações adequadas.', outcomes: ['Login', 'Permissões', 'Perfis'], panelTitle: 'Área autenticada' },
      { title: 'Acompanhar serviços', description: 'Status e histórico ficam disponíveis em tempo real.', longDescription: 'Solicitações, entregas e etapas do serviço podem ser apresentadas em uma linha clara para o cliente.', outcomes: ['Status', 'Histórico', 'Atualizações'], panelTitle: 'Acompanhamento' },
      { title: 'Resolver sozinho', description: 'Documentos e cobranças ficam acessíveis quando necessário.', longDescription: 'O cliente baixa arquivos, consulta pagamentos e abre solicitações sem depender do horário do atendimento.', outcomes: ['Documentos', 'Pagamentos', 'Solicitações'], panelTitle: 'Autosserviço' }
    ],
    modulesTitle: 'Uma área do cliente com a cara da sua operação.',
    modulesDescription: 'O portal pode expor somente o essencial ou concentrar uma jornada completa de relacionamento.',
    modules: [
      { symbol: '🔒', title: 'Autenticação', description: 'Acesso seguro com perfis e regras por cliente.' },
      { symbol: '▤', title: 'Documentos', description: 'Contratos, comprovantes, relatórios e arquivos organizados.' },
      { symbol: '→', title: 'Solicitações', description: 'Abertura e acompanhamento de pedidos com status e histórico.' },
      { symbol: '$', title: 'Pagamentos', description: 'Cobranças, vencimentos, comprovantes e links quando aplicável.' },
      { symbol: '◌', title: 'Status', description: 'Visão clara sobre andamento, etapas e próximos passos.' },
      { symbol: '✉', title: 'Comunicação', description: 'Avisos e notificações vinculados ao contexto do cliente.' }
    ],
    benefitsTitle: 'Mais autonomia para o cliente. Menos demanda repetitiva para a equipe.',
    benefits: ['Informação disponível fora do horário comercial.', 'Menos solicitações simples no atendimento.', 'Experiência consistente e profissional.', 'Histórico e documentos centralizados.'],
    integrations: ['CRM', 'Financeiro', 'ERP', 'Assinatura digital', 'E-mail', 'WhatsApp'],
    faq: [
      { question: 'Cada cliente vê só os próprios dados?', answer: 'Sim. O portal é pensado com autenticação e regras de acesso para separar informações por conta ou usuário.' },
      { question: 'Pode ter pagamento dentro do portal?', answer: 'Sim, por integração com gateways ou sistemas financeiros adequados ao cenário.' },
      { question: 'É possível personalizar com minha marca?', answer: 'Sim. Visual, conteúdos, módulos e experiência podem seguir a identidade e jornada da empresa.' }
    ],
    ctaTitle: 'Seu cliente pode ter uma experiência tão organizada quanto sua operação.',
    ctaDescription: 'Construímos o portal em cima dos serviços, dados e informações que fazem sentido disponibilizar para cada cliente.'
  },

  'landing-page': {
    category: 'Landing Page', productName: 'Launch Page',
    heroTitle: 'Uma página criada para transformar atenção em ação.',
    heroDescription: 'Landing page sob medida para campanhas, serviços, lançamentos e captação de leads, com mensagem clara, prova, velocidade e um caminho de conversão bem definido.',
    accent: '#7c3aed', accentSoft: '#ede9fe', preview: 'landing',
    metrics: [{ value: '1 objetivo', label: 'por página' }, { value: 'mobile first', label: 'desde o primeiro bloco' }, { value: '+clareza', label: 'até o CTA' }],
    heroChips: ['Hero', 'Oferta', 'Benefícios', 'Prova social', 'FAQ', 'CTA'],
    problemTitle: 'Tráfego sozinho não converte. A página precisa conduzir a decisão.',
    problemDescription: 'Uma landing page eficiente reduz distrações, apresenta valor rapidamente e organiza os argumentos certos até a ação principal.',
    pains: [
      { title: 'Mensagem genérica', description: 'O visitante chega, mas não entende em poucos segundos o que está sendo oferecido e por que aquilo importa.' },
      { title: 'Página sem direção', description: 'Muitos caminhos, links e informações competem entre si e enfraquecem o objetivo principal da campanha.' },
      { title: 'Confiança insuficiente', description: 'Sem prova, contexto e respostas para objeções, o interesse não se transforma em contato ou compra.' }
    ],
    storyTitle: 'Uma narrativa curta, visual e construída para levar até o próximo passo.',
    storyDescription: 'A landing page organiza a experiência em uma sequência intencional: chamar atenção, construir valor e remover fricção antes do CTA.',
    storySteps: [
      { title: 'Prender a atenção', description: 'Hero comunica oferta e diferencial logo no primeiro contato.', longDescription: 'Headline, apoio visual, proposta de valor e CTA trabalham juntos para que o visitante entenda rapidamente o que existe ali e o que fazer depois.', outcomes: ['Headline', 'Proposta de valor', 'CTA principal'], panelTitle: 'Primeira dobra' },
      { title: 'Construir confiança', description: 'Benefícios e prova sustentam a decisão.', longDescription: 'A página apresenta benefícios concretos, diferenciais, depoimentos, números, cases ou elementos de credibilidade de acordo com o produto.', outcomes: ['Benefícios', 'Prova social', 'Diferenciais'], panelTitle: 'Argumentação' },
      { title: 'Converter', description: 'A ação final fica simples, visível e mensurável.', longDescription: 'Formulário, WhatsApp, checkout ou agendamento são posicionados com clareza e conectados às ferramentas necessárias para registrar cada conversão.', outcomes: ['Formulário', 'WhatsApp', 'Analytics'], panelTitle: 'Conversão' }
    ],
    modulesTitle: 'Uma landing page enxuta por fora e estrategicamente completa por dentro.',
    modulesDescription: 'Os blocos são escolhidos de acordo com oferta, público, origem do tráfego e tipo de conversão desejada.',
    modules: [
      { symbol: 'Aa', title: 'Hero e oferta', description: 'Headline, subheadline, proposta de valor e CTA principal.' },
      { symbol: '✦', title: 'Benefícios', description: 'Argumentos organizados para mostrar valor de forma rápida e escaneável.' },
      { symbol: '★', title: 'Prova social', description: 'Depoimentos, números, clientes, cases ou sinais de confiança.' },
      { symbol: '↗', title: 'Conversão', description: 'Formulário, WhatsApp, agendamento ou checkout conforme o objetivo.' },
      { symbol: '◎', title: 'Analytics', description: 'Eventos e métricas para acompanhar origem, cliques e conversões.' },
      { symbol: '?', title: 'FAQ e objeções', description: 'Respostas às dúvidas que normalmente impedem o visitante de avançar.' }
    ],
    benefitsTitle: 'Mais clareza para quem chega. Mais intenção em cada clique.',
    benefits: ['Experiência focada em um único objetivo.', 'Design responsivo e pensado para mobile.', 'Mensagem alinhada à campanha e ao público.', 'Base pronta para mensuração e otimizações futuras.'],
    integrations: ['WhatsApp', 'CRM', 'Meta Pixel', 'Google Analytics 4', 'Google Tag Manager', 'E-mail'],
    faq: [
      { question: 'Pode ter formulário e WhatsApp ao mesmo tempo?', answer: 'Sim. A conversão pode usar um ou mais canais, desde que a hierarquia da página mantenha o objetivo principal claro.' },
      { question: 'Vocês configuram métricas?', answer: 'A página pode ser preparada para eventos de analytics, pixels e tags de conversão de acordo com as ferramentas usadas na campanha.' },
      { question: 'Pode seguir a identidade visual da minha marca?', answer: 'Sim. Tipografia, cores, linguagem, componentes e direção visual são adaptados à identidade e à oferta.' }
    ],
    ctaTitle: 'Sua próxima campanha merece uma página construída para ela.',
    ctaDescription: 'Desenhamos a landing page em torno da oferta, do público e da ação que realmente importa para o negócio.'
  },

  'hub-integracoes': {
    category: 'Integrações', productName: 'Connect Hub',
    heroTitle: 'Seus sistemas podem conversar sem trabalho manual no meio.',
    heroDescription: 'Uma camada de integrações para conectar APIs, webhooks e serviços, com monitoramento de eventos e sincronizações.',
    accent: '#0891b2', accentSoft: '#cffafe', preview: 'integrations',
    metrics: [{ value: 'APIs', label: 'conectadas em um hub' }, { value: '+controle', label: 'sobre falhas e eventos' }, { value: '-manual', label: 'na troca de dados' }],
    heroChips: ['APIs', 'Webhooks', 'Logs', 'Sync', 'Filas', 'Monitoramento'],
    problemTitle: 'Integração invisível quando funciona. Crítica quando falha.',
    problemDescription: 'Conexões importantes precisam de tratamento de erro, rastreabilidade e monitoramento — não apenas chamadas soltas entre sistemas.',
    pains: [
      { title: 'Dados duplicados', description: 'Informações são copiadas manualmente entre sistemas e geram divergência ao longo do tempo.' },
      { title: 'Falhas silenciosas', description: 'Quando uma integração quebra, a equipe percebe pelo efeito e não pela causa.' },
      { title: 'Conexões frágeis', description: 'Scripts pontuais crescem sem observabilidade, fila ou estratégia de recuperação.' }
    ],
    storyTitle: 'Conectar, observar e recuperar faz parte do mesmo desenho.',
    storyDescription: 'O hub organiza integrações como uma camada da arquitetura, com eventos visíveis e regras de sincronização.',
    storySteps: [
      { title: 'Receber eventos', description: 'APIs e webhooks alimentam o hub.', longDescription: 'Eventos entram por endpoints controlados, com validação, autenticação e identificação da origem.', outcomes: ['Webhooks', 'APIs', 'Validação'], panelTitle: 'Entrada de eventos' },
      { title: 'Processar e sincronizar', description: 'Filas e regras tratam dados de forma segura.', longDescription: 'A camada intermediária transforma, enfileira e distribui dados para evitar acoplamento entre sistemas.', outcomes: ['Filas', 'Transformação', 'Retries'], panelTitle: 'Orquestração' },
      { title: 'Monitorar', description: 'Logs e alertas mostram saúde e falhas.', longDescription: 'Painéis deixam claro o que foi processado, o que falhou e o que precisa de intervenção.', outcomes: ['Logs', 'Alertas', 'Métricas'], panelTitle: 'Observabilidade' }
    ],
    modulesTitle: 'Uma infraestrutura de integração feita para crescer.',
    modulesDescription: 'A solução pode conectar dois sistemas ou servir como base para várias integrações críticas da operação.',
    modules: [
      { symbol: '↔', title: 'APIs', description: 'Conectores REST ou outros padrões adequados ao serviço utilizado.' },
      { symbol: '⚡', title: 'Webhooks', description: 'Recebimento e disparo de eventos com validação e rastreabilidade.' },
      { symbol: '≋', title: 'Filas', description: 'Processamento desacoplado para lidar melhor com volume e indisponibilidade.' },
      { symbol: '↺', title: 'Retries', description: 'Tentativas e estratégias de recuperação para falhas temporárias.' },
      { symbol: '▤', title: 'Logs', description: 'Histórico técnico de eventos e processamento para investigação.' },
      { symbol: '!', title: 'Alertas', description: 'Avisos sobre falhas, atraso ou comportamento fora do esperado.' }
    ],
    benefitsTitle: 'Menos cópia de dados. Mais confiabilidade entre sistemas.',
    benefits: ['Sincronização automática entre ferramentas.', 'Falhas visíveis e rastreáveis.', 'Arquitetura preparada para novas integrações.', 'Menos dependência de tarefas manuais entre áreas.'],
    integrations: ['WhatsApp', 'Open Finance', 'ERP', 'CRM', 'Pagamentos', 'E-mail'],
    faq: [
      { question: 'Dá para integrar qualquer sistema?', answer: 'Depende do acesso disponível. APIs, webhooks, exportações ou acesso controlado a dados definem o que é tecnicamente possível.' },
      { question: 'O hub mostra quando algo falha?', answer: 'Sim. Logs, métricas e alertas podem fazer parte da solução para evitar falhas silenciosas.' },
      { question: 'Pode sincronizar em tempo real?', answer: 'Quando os sistemas envolvidos suportam eventos ou APIs adequadas, sincronizações podem acontecer em tempo real ou quase real.' }
    ],
    ctaTitle: 'Seus sistemas podem parar de trabalhar em ilhas.',
    ctaDescription: 'Mapeamos origem, destino, regras e criticidade de cada fluxo para construir integrações confiáveis e observáveis.'
  },

  'painel-executivo': {
    category: 'Gestão', productName: 'Executive View',
    heroTitle: 'A gestão não precisa procurar o número certo. Ele precisa estar na tela certa.',
    heroDescription: 'Painel executivo para reunir indicadores essenciais, metas e tendências em uma visão limpa para decisão rápida.',
    accent: '#ca8a04', accentSoft: '#fef9c3', preview: 'executive',
    metrics: [{ value: 'KPIs', label: 'em uma visão única' }, { value: 'tempo real', label: 'quando a fonte permite' }, { value: '+foco', label: 'no que muda decisão' }],
    heroChips: ['KPIs', 'Metas', 'Forecast', 'Comparativos', 'Alertas', 'Drill-down'],
    problemTitle: 'Um dashboard bom não mostra tudo. Mostra o que muda uma decisão.',
    problemDescription: 'Quando cada área apresenta seus próprios relatórios, a gestão perde tempo reconciliando versões e procurando significado por trás dos números.',
    pains: [
      { title: 'Indicadores sem padrão', description: 'Cada área calcula e apresenta dados de um jeito, dificultando comparação e confiança.' },
      { title: 'Relatórios estáticos', description: 'A análise depende de arquivos atualizados manualmente e não acompanha mudanças rapidamente.' },
      { title: 'Excesso de informação', description: 'Muitos números aparecem sem hierarquia, contexto ou prioridade para a decisão.' }
    ],
    storyTitle: 'Do dado operacional à visão de liderança.',
    storyDescription: 'O painel seleciona, organiza e apresenta indicadores com contexto suficiente para orientar decisões e aprofundar quando necessário.',
    storySteps: [
      { title: 'Definir o que importa', description: 'KPIs seguem objetivos e decisões reais.', longDescription: 'O primeiro passo é separar métricas de vaidade de indicadores que realmente orientam gestão, metas e risco.', outcomes: ['KPIs', 'Metas', 'Critérios'], panelTitle: 'Mapa de indicadores' },
      { title: 'Conectar fontes', description: 'Dados operacionais alimentam a visão executiva.', longDescription: 'As fontes são integradas e normalizadas para reduzir versões conflitantes do mesmo indicador.', outcomes: ['Fontes', 'Atualização', 'Consistência'], panelTitle: 'Camada de dados' },
      { title: 'Ler e aprofundar', description: 'A visão começa simples e permite investigar.', longDescription: 'Cards e tendências mostram o panorama; filtros e drill-down ajudam a entender o que está por trás de um desvio.', outcomes: ['Tendências', 'Comparativos', 'Drill-down'], panelTitle: 'Visão executiva' }
    ],
    modulesTitle: 'Indicadores organizados pela decisão que precisam apoiar.',
    modulesDescription: 'O painel pode combinar finanças, vendas, atendimento, operação e outras áreas na mesma leitura gerencial.',
    modules: [
      { symbol: 'K', title: 'KPIs', description: 'Indicadores principais com contexto, meta e evolução.' },
      { symbol: '◎', title: 'Metas', description: 'Progresso por ciclo, área ou objetivo estratégico.' },
      { symbol: '↗', title: 'Tendências', description: 'Evolução temporal para identificar movimento e desvio.' },
      { symbol: '⇄', title: 'Comparativos', description: 'Períodos, unidades, equipes ou cenários lado a lado.' },
      { symbol: '!', title: 'Alertas', description: 'Destaque automático para indicadores fora do intervalo esperado.' },
      { symbol: '+', title: 'Drill-down', description: 'Detalhamento para investigar a composição do número principal.' }
    ],
    benefitsTitle: 'Uma tela para alinhar leitura, prioridade e decisão.',
    benefits: ['Indicadores com definição consistente.', 'Menos tempo consolidando relatórios.', 'Leitura rápida de tendências e desvios.', 'Visões adaptadas por perfil de liderança.'],
    integrations: ['ERP', 'CRM', 'Financeiro', 'Planilhas', 'Banco de dados', 'APIs'],
    faq: [
      { question: 'O painel atualiza sozinho?', answer: 'Pode atualizar automaticamente quando as fontes de dados disponibilizam integração adequada.' },
      { question: 'Cada diretoria pode ter uma visão?', answer: 'Sim. Perfis diferentes podem visualizar indicadores e níveis de detalhe distintos.' },
      { question: 'Dá para usar dados de planilhas?', answer: 'Sim, embora integrações mais estruturadas tendam a ser mais confiáveis. A fonte ideal depende do cenário atual.' }
    ],
    ctaTitle: 'A próxima reunião pode começar pelo insight, não pela conferência dos números.',
    ctaDescription: 'Definimos indicadores, fontes e visualizações para construir uma visão executiva que faça sentido para sua gestão.'
  },

  'automacao-processos': {
    category: 'Integrações', productName: 'Flow Automation',
    heroTitle: 'Tudo que é repetitivo pode virar um fluxo previsível.',
    heroDescription: 'Automações para conectar gatilhos, regras e ações entre sistemas, reduzindo tarefas manuais e atrasos operacionais.',
    accent: '#9333ea', accentSoft: '#f3e8ff', preview: 'automation',
    metrics: [{ value: '24/7', label: 'execução de rotinas' }, { value: '-manual', label: 'em tarefas repetitivas' }, { value: '+SLA', label: 'em processos previsíveis' }],
    heroChips: ['Gatilhos', 'Regras', 'Ações', 'Filas', 'Alertas', 'Logs'],
    problemTitle: 'Se uma tarefa acontece sempre do mesmo jeito, ela não deveria depender sempre de alguém.',
    problemDescription: 'Automação bem desenhada reduz esforço sem esconder o processo: gatilhos, decisões e ações ficam explícitos e monitoráveis.',
    pains: [
      { title: 'Rotinas repetitivas', description: 'Pessoas copiam dados, enviam avisos e atualizam sistemas seguindo sempre os mesmos passos.' },
      { title: 'Atraso entre áreas', description: 'Uma etapa depende de alguém lembrar de avisar a próxima pessoa ou sistema.' },
      { title: 'Baixa rastreabilidade', description: 'Quando algo não acontece, é difícil saber onde o fluxo parou e o que deveria ter sido executado.' }
    ],
    storyTitle: 'Gatilho, regra e ação: simples de entender, poderoso para operar.',
    storyDescription: 'Workflows podem automatizar desde uma notificação simples até sequências entre diferentes sistemas.',
    storySteps: [
      { title: 'Detectar', description: 'Um evento inicia o processo automaticamente.', longDescription: 'Mudança de status, horário, novo registro ou webhook podem ser usados como gatilho da automação.', outcomes: ['Eventos', 'Agendamentos', 'Webhooks'], panelTitle: 'Gatilhos' },
      { title: 'Decidir', description: 'Regras determinam o que deve acontecer.', longDescription: 'Condições e validações aplicam a lógica do negócio antes de executar qualquer ação.', outcomes: ['Condições', 'Validações', 'Branches'], panelTitle: 'Motor de regras' },
      { title: 'Executar e registrar', description: 'Ações acontecem e ficam rastreáveis.', longDescription: 'O fluxo atualiza sistemas, envia mensagens ou cria tarefas e registra o resultado para monitoramento.', outcomes: ['Ações', 'Logs', 'Alertas'], panelTitle: 'Execução do workflow' }
    ],
    modulesTitle: 'Workflows para tirar tarefas repetitivas do caminho.',
    modulesDescription: 'Cada automação é desenhada com os limites e exceções do processo real, não apenas com o cenário ideal.',
    modules: [
      { symbol: '⚡', title: 'Gatilhos', description: 'Eventos de sistema, horários, webhooks ou mudanças de estado.' },
      { symbol: '?', title: 'Condições', description: 'Regras e validações que decidem o caminho do workflow.' },
      { symbol: '→', title: 'Ações', description: 'Atualizações, mensagens, tarefas e chamadas para outros sistemas.' },
      { symbol: '⏱', title: 'Agendamentos', description: 'Execuções por horário, atraso ou recorrência.' },
      { symbol: '↺', title: 'Retries', description: 'Tratamento de falhas temporárias e reprocessamento controlado.' },
      { symbol: '▤', title: 'Logs', description: 'Histórico de execução para acompanhar sucesso e investigar falhas.' }
    ],
    benefitsTitle: 'A operação continua andando mesmo quando ninguém está clicando.',
    benefits: ['Menos tarefas repetitivas para a equipe.', 'Processos mais consistentes e previsíveis.', 'Redução de atrasos entre etapas.', 'Rastreabilidade sobre o que foi executado.'],
    integrations: ['WhatsApp', 'CRM', 'ERP', 'Financeiro', 'E-mail', 'Webhooks'],
    faq: [
      { question: 'Qualquer processo pode ser automatizado?', answer: 'Nem todo processo deve ser automatizado. Priorizamos tarefas repetitivas, previsíveis e com regras suficientemente claras.' },
      { question: 'O fluxo pode pedir aprovação humana?', answer: 'Sim. Uma automação pode pausar e aguardar aprovação antes de executar ações críticas.' },
      { question: 'Consigo saber quando uma automação falha?', answer: 'Sim. Logs e alertas podem ser incluídos para tornar o processo observável e facilitar reprocessamento.' }
    ],
    ctaTitle: 'Seu próximo ganho de produtividade pode estar em uma tarefa que a equipe repete todo dia.',
    ctaDescription: 'Mapeamos o processo, identificamos pontos seguros de automação e conectamos as ferramentas necessárias.'
  },

  'saas-mvp': {
    category: 'Produto digital', productName: 'Launch OS',
    heroTitle: 'Da ideia a um produto que usuários conseguem testar de verdade.',
    heroDescription: 'MVP de SaaS com base sólida para autenticação, planos, workspace, usuários e os módulos essenciais do produto.',
    accent: '#dc2626', accentSoft: '#fee2e2', preview: 'saas',
    metrics: [{ value: 'MVP', label: 'focado no valor central' }, { value: '4–8 sem.', label: 'referência para escopo enxuto' }, { value: '+base', label: 'para evoluir depois' }],
    heroChips: ['Login', 'Workspace', 'Planos', 'Usuários', 'Billing', 'Dashboard'],
    problemTitle: 'O MVP precisa validar a proposta, não reproduzir a visão final inteira.',
    problemDescription: 'Um primeiro produto bem recortado entrega o núcleo da experiência, coleta uso real e cria uma base técnica que pode evoluir sem jogar tudo fora.',
    pains: [
      { title: 'Escopo grande demais', description: 'A primeira versão tenta resolver todos os cenários antes mesmo de validar o comportamento principal.' },
      { title: 'Base improvisada', description: 'Pressa leva a decisões que dificultam autenticação, planos, usuários e evolução depois do lançamento.' },
      { title: 'Pouca validação', description: 'Muito tempo é gasto construindo antes de colocar algo funcional nas mãos de usuários reais.' }
    ],
    storyTitle: 'Construir o núcleo, lançar e aprender com uso real.',
    storyDescription: 'O MVP combina estrutura de produto e foco de escopo para chegar à validação sem criar uma dívida desnecessária.',
    storySteps: [
      { title: 'Definir o núcleo', description: 'A proposta de valor vira um fluxo testável.', longDescription: 'Mapeamos usuário, problema principal e jornada mínima para chegar a uma primeira versão coerente.', outcomes: ['Usuário-alvo', 'Fluxo principal', 'Escopo MVP'], panelTitle: 'Product scope' },
      { title: 'Construir a base', description: 'Produto nasce com autenticação e estrutura para crescer.', longDescription: 'Arquitetura, dados, permissões e infraestrutura são dimensionados para o MVP sem bloquear evolução posterior.', outcomes: ['Autenticação', 'Dados', 'Deploy'], panelTitle: 'Product foundation' },
      { title: 'Lançar e aprender', description: 'Uso real orienta a próxima versão.', longDescription: 'Eventos, feedback e comportamento dos usuários ajudam a priorizar melhorias e novos módulos com evidência.', outcomes: ['Analytics', 'Feedback', 'Roadmap'], panelTitle: 'Validation loop' }
    ],
    modulesTitle: 'A base comum de um produto SaaS moderno.',
    modulesDescription: 'Nem todo MVP precisa de tudo no início, mas estes blocos ajudam a estruturar produtos que podem evoluir.',
    modules: [
      { symbol: '🔒', title: 'Autenticação', description: 'Login, recuperação de acesso e proteção das áreas privadas.' },
      { symbol: 'W', title: 'Workspaces', description: 'Organização de usuários e dados por conta, empresa ou equipe.' },
      { symbol: 'U', title: 'Usuários', description: 'Convites, perfis e permissões dentro de cada organização.' },
      { symbol: '$', title: 'Planos e billing', description: 'Estrutura para planos, limites e cobrança quando fizer sentido.' },
      { symbol: '◎', title: 'Dashboard', description: 'Visão principal alinhada ao valor que o produto entrega.' },
      { symbol: '↗', title: 'Analytics', description: 'Eventos de uso e métricas para acompanhar validação do produto.' }
    ],
    benefitsTitle: 'Uma primeira versão pequena o bastante para lançar e boa o bastante para aprender.',
    benefits: ['Escopo guiado pela proposta de valor.', 'Base técnica preparada para evolução.', 'Experiência consistente desde a primeira versão.', 'Feedback real entrando mais cedo no roadmap.'],
    integrations: ['Pagamentos', 'E-mail', 'Analytics', 'WhatsApp', 'APIs', 'Storage'],
    faq: [
      { question: 'O MVP precisa ter cobrança?', answer: 'Não necessariamente. Billing entra apenas se fizer parte da hipótese que precisa ser validada na primeira versão.' },
      { question: 'Depois dá para continuar evoluindo?', answer: 'Sim. A ideia é construir uma base que possa receber novos módulos, integrações e refinamentos sem recomeçar do zero.' },
      { question: 'O prazo é sempre 4–8 semanas?', answer: 'Não. Essa é apenas uma referência para escopos enxutos. O prazo real depende de regras, integrações, design e complexidade técnica.' }
    ],
    ctaTitle: 'Sua ideia não precisa esperar o produto final para começar a ser validada.',
    ctaDescription: 'Recortamos o MVP, definimos a experiência central e construímos uma versão funcional pronta para evoluir.'
  }
}

const fallback = solutions['dashboard-financeiro']
const solution = computed(() => solutions[props.slug] || fallback)

const themeStyle = computed(() => ({
  '--accent': solution.value.accent,
  '--accent-soft': solution.value.accentSoft
}))

const process = [
  { title: 'Diagnóstico', description: 'Entendemos processo, gargalos, usuários e integrações que já fazem parte da operação.' },
  { title: 'Experiência', description: 'Desenhamos fluxo, módulos e interface para deixar claro como a solução será utilizada.' },
  { title: 'Construção', description: 'Desenvolvimento acontece em ciclos curtos com validação e prioridade sobre o que gera valor.' },
  { title: 'Evolução', description: 'Depois do lançamento, uso real e novas necessidades orientam as próximas versões.' }
]

const activeStoryStep = computed(() => solution.value.storySteps[activeStep.value] || solution.value.storySteps[0])

const phone = '77999631644'
const whatsHref = computed(() => {
  const text = [
    'Olá! Vi uma apresentação de solução no site e quero conversar sobre algo nessa linha.',
    '',
    `Modelo: ${solution.value.heroTitle}`,
    `Categoria: ${solution.value.category}`,
    '',
    'Quero adaptar essa ideia para minha operação.'
  ].join('\n')

  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
})

function moduleSpan(index) {
  const layout = ['xl:col-span-7', 'xl:col-span-5 xl:mt-8', 'xl:col-span-5', 'xl:col-span-7 xl:mt-8', 'xl:col-span-6', 'xl:col-span-6 xl:mt-6']
  return layout[index % layout.length]
}

function card(title, value, muted = false) {
  return h('div', { class: muted ? 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' : 'rounded-2xl bg-white/[0.07] p-4 ring-1 ring-white/10' }, [
    h('p', { class: 'text-[10px] font-medium text-zinc-500' }, title),
    h('p', { class: 'mt-2 text-lg font-semibold text-white' }, value)
  ])
}

const FinancePreview = defineComponent(() => () => h('div', { class: 'grid h-full gap-4' }, [
  h('div', { class: 'flex items-center justify-between' }, [
    h('div', [h('p', { class: 'text-xs text-zinc-500' }, 'Visão geral'), h('p', { class: 'mt-1 text-xl font-semibold' }, 'Financeiro')]),
    h('span', { class: 'rounded-full bg-white/10 px-3 py-1.5 text-[10px] font-semibold text-zinc-400' }, '30 dias')
  ]),
  h('div', { class: 'grid grid-cols-3 gap-2' }, [card('Saldo', 'R$ 84,3k'), card('Receitas', 'R$ 128k'), card('Margem', '31,8%')]),
  h('div', { class: 'grid flex-1 grid-cols-[1.35fr_.65fr] gap-3' }, [
    h('div', { class: 'flex items-end gap-2 rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [42, 65, 54, 82, 71, 92, 78, 100].map((v, i) => h('span', { class: 'flex-1 rounded-t-md', style: { height: `${v}%`, background: i % 2 ? 'var(--accent)' : 'rgba(255,255,255,.7)' } }))),
    h('div', { class: 'space-y-3 rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [
      h('p', { class: 'text-xs font-semibold text-zinc-400' }, 'Despesas'),
      ...['Equipe 42%', 'Estrutura 28%', 'Insumos 18%', 'Software 12%'].map(t => h('div', { class: 'rounded-xl bg-white/[0.06] p-3 text-[11px] text-zinc-300' }, t))
    ])
  ])
]))

const CrmPreview = defineComponent(() => () => h('div', { class: 'grid h-full gap-4' }, [
  h('div', { class: 'flex items-center justify-between' }, [h('div', [h('p', { class: 'text-xs text-zinc-500' }, 'Pipeline'), h('p', { class: 'mt-1 text-xl font-semibold' }, 'Oportunidades')]), h('span', { class: 'rounded-full bg-white/10 px-3 py-1.5 text-[10px] font-semibold text-zinc-400' }, 'R$ 186k')]),
  h('div', { class: 'grid flex-1 grid-cols-3 gap-3' }, ['Novos', 'Proposta', 'Fechamento'].map((col, idx) => h('div', { class: 'rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10' }, [
    h('p', { class: 'text-[10px] font-semibold text-zinc-500' }, col),
    ...Array.from({ length: idx === 1 ? 3 : 2 }, (_, i) => h('div', { class: 'mt-3 rounded-xl bg-white/[0.07] p-3 ring-1 ring-white/10' }, [
      h('span', { class: 'block h-2 rounded-full bg-white/30', style: { width: i % 2 ? '55%' : '78%' } }), h('span', { class: 'mt-3 block h-2 w-1/2 rounded-full bg-white/10' }), h('span', { class: 'mt-4 block text-xs font-semibold text-white' }, `R$ ${[6800, 12400, 9200][(i + idx) % 3].toLocaleString('pt-BR')}`)
    ]))
  ])))
]))

const InboxPreview = defineComponent(() => () => h('div', { class: 'grid h-full grid-cols-[.8fr_1.2fr] gap-3' }, [
  h('div', { class: 'rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10' }, [
    h('p', { class: 'text-xs font-semibold text-zinc-400' }, 'Conversas'),
    ...['Mariana Costa', 'Carlos Menezes', 'Clínica Prisma', 'João Lima'].map((name, i) => h('div', { class: `mt-3 rounded-xl p-3 ${i === 0 ? 'bg-white text-zinc-950' : 'bg-white/[0.05] text-white'}` }, [h('p', { class: 'text-xs font-semibold' }, name), h('p', { class: `mt-1 text-[9px] ${i === 0 ? 'text-zinc-500' : 'text-zinc-500'}` }, i === 0 ? 'Queria confirmar meu horário...' : 'Última mensagem do cliente')]))
  ]),
  h('div', { class: 'flex flex-col rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [
    h('div', { class: 'border-b border-white/10 pb-3' }, [h('p', { class: 'text-sm font-semibold' }, 'Mariana Costa'), h('p', { class: 'mt-1 text-[10px] text-zinc-500' }, 'WhatsApp · Online')]),
    h('div', { class: 'flex-1 space-y-3 py-4' }, [
      h('div', { class: 'mr-10 rounded-2xl rounded-bl-md bg-white/[0.08] p-3 text-[11px] text-zinc-300' }, 'Olá! Queria confirmar meu horário de amanhã.'),
      h('div', { class: 'ml-10 rounded-2xl rounded-br-md p-3 text-[11px] text-white', style: { background: 'var(--accent)' } }, 'Oi, Mariana! Está confirmado para 15h30.'),
      h('div', { class: 'mr-16 rounded-2xl rounded-bl-md bg-white/[0.08] p-3 text-[11px] text-zinc-300' }, 'Perfeito, obrigada!')
    ]),
    h('div', { class: 'h-10 rounded-full bg-white/[0.08] ring-1 ring-white/10' })
  ])
]))

const AiPreview = defineComponent(() => () => h('div', { class: 'grid h-full grid-cols-[1fr_.55fr] gap-3' }, [
  h('div', { class: 'flex flex-col rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [
    h('div', [h('p', { class: 'text-xs text-zinc-500' }, 'Assistente operacional'), h('p', { class: 'mt-1 text-xl font-semibold' }, 'Nexa AI')]),
    h('div', { class: 'flex-1 space-y-3 py-5' }, [
      h('div', { class: 'mr-14 rounded-2xl bg-white/[0.07] p-3 text-[11px] text-zinc-300' }, 'Quais clientes precisam de retorno hoje?'),
      h('div', { class: 'ml-6 rounded-2xl bg-white p-4 text-[11px] leading-5 text-zinc-700' }, 'Encontrei 5 clientes com follow-up pendente. Três estão acima do SLA definido. Posso organizar por prioridade e preparar as mensagens.'),
      h('div', { class: 'flex gap-2' }, ['CRM', 'Financeiro', 'WhatsApp'].map(t => h('span', { class: 'rounded-full bg-white/[0.08] px-2 py-1 text-[9px] font-semibold text-zinc-400' }, t)))
    ]),
    h('div', { class: 'h-10 rounded-full bg-white/[0.08] ring-1 ring-white/10' })
  ]),
  h('div', { class: 'space-y-3' }, [
    h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('p', { class: 'text-xs font-semibold text-zinc-400' }, 'Ferramentas'), ...['CRM', 'Financeiro', 'WhatsApp'].map(t => h('div', { class: 'mt-3 flex items-center justify-between text-[11px] text-zinc-300' }, [t, h('span', { class: 'h-2 w-2 rounded-full bg-emerald-400' })]))]),
    h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('p', { class: 'text-xs font-semibold text-zinc-400' }, 'Contexto'), h('p', { class: 'mt-4 text-2xl font-semibold' }, '248'), h('p', { class: 'mt-1 text-[10px] text-zinc-500' }, 'documentos indexados')])
  ])
]))

const BackofficePreview = defineComponent(() => () => h('div', { class: 'grid h-full grid-cols-[.45fr_1fr] gap-3' }, [
  h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('div', { class: 'h-10 w-10 rounded-xl', style: { background: 'var(--accent)' } }), ...['Visão geral', 'Usuários', 'Processos', 'Relatórios', 'Auditoria'].map((t, i) => h('div', { class: `mt-4 rounded-lg px-3 py-2 text-[10px] ${i === 1 ? 'bg-white text-zinc-950' : 'text-zinc-500'}` }, t))]),
  h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [
    h('div', { class: 'grid grid-cols-3 gap-2' }, [card('Usuários', '32'), card('Processos', '9'), card('SLA', '96%')]),
    h('div', { class: 'mt-4 overflow-hidden rounded-xl ring-1 ring-white/10' }, [
      h('div', { class: 'grid grid-cols-[1.3fr_.7fr_.5fr] gap-2 bg-white/[0.08] p-3 text-[9px] font-semibold text-zinc-500' }, [h('span', 'Usuário'), h('span', 'Perfil'), h('span', 'Status')]),
      ...['Gabrielle Motta', 'Lucas Andrade', 'Marina Alves', 'Rafael Souza'].map((t, i) => h('div', { class: 'grid grid-cols-[1.3fr_.7fr_.5fr] gap-2 border-t border-white/10 p-3 text-[10px]' }, [h('span', { class: 'font-semibold' }, t), h('span', { class: 'text-zinc-500' }, ['Admin', 'Financeiro', 'Atendimento', 'Operação'][i]), h('span', { class: i === 3 ? 'text-zinc-500' : 'text-emerald-400' }, i === 3 ? 'Inativo' : 'Ativo')]))
    ])
  ])
]))

const CalendarPreview = defineComponent(() => () => h('div', { class: 'grid h-full grid-cols-[1fr_.5fr] gap-3' }, [
  h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [
    h('div', { class: 'flex items-center justify-between' }, [h('p', { class: 'text-sm font-semibold' }, 'Setembro 2026'), h('span', { class: 'text-[10px] text-zinc-500' }, 'Semana')]),
    h('div', { class: 'mt-4 grid grid-cols-7 gap-1' }, Array.from({ length: 35 }, (_, i) => h('div', { class: `aspect-square rounded-lg ${[17, 18, 24].includes(i) ? 'text-white' : 'bg-white/[0.05] text-zinc-500'} grid place-items-center text-[9px]`, style: [17, 18, 24].includes(i) ? { background: 'var(--accent)' } : {} }, String((i % 30) + 1)))),
    h('div', { class: 'mt-4 space-y-2' }, ['08:30 · Mariana Costa', '10:00 · Lucas Lima', '14:30 · Carla Menezes'].map(t => h('div', { class: 'rounded-xl bg-white/[0.06] p-3 text-[10px] text-zinc-300 ring-1 ring-white/10' }, t)))
  ]),
  h('div', { class: 'space-y-3' }, [card('Hoje', '8 atendimentos'), card('Confirmados', '6'), card('Pendentes', '2')])
]))

const PortalPreview = defineComponent(() => () => h('div', { class: 'grid h-full gap-4' }, [
  h('div', { class: 'rounded-2xl p-5 text-white ring-1 ring-white/10', style: { background: 'linear-gradient(135deg,var(--accent),#111827)' } }, [h('p', { class: 'text-[10px] text-white/70' }, 'Bem-vinda de volta'), h('p', { class: 'mt-2 text-xl font-semibold' }, 'Mariana Costa'), h('p', { class: 'mt-1 text-[10px] text-white/60' }, 'Cliente desde 2024')]),
  h('div', { class: 'grid grid-cols-3 gap-3' }, [['Documentos', '8 arquivos'], ['Solicitações', '2 abertas'], ['Financeiro', 'Em dia']].map(([a, b]) => h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('p', { class: 'text-[10px] text-zinc-500' }, a), h('p', { class: 'mt-2 text-sm font-semibold' }, b)]))),
  h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('p', { class: 'text-xs font-semibold text-zinc-400' }, 'Atividade recente'), ...['Relatório mensal disponível', 'Solicitação #2048 atualizada', 'Mensalidade de setembro gerada'].map(t => h('div', { class: 'mt-3 flex items-center gap-3 text-[10px] text-zinc-300' }, [h('span', { class: 'h-2 w-2 rounded-full', style: { background: 'var(--accent)' } }), t]))])
]))

const LandingPreview = defineComponent(() => () => h('div', { class: 'h-full overflow-hidden rounded-2xl bg-white text-zinc-950 ring-1 ring-white/10' }, [
  h('div', { class: 'flex items-center justify-between border-b border-zinc-100 px-5 py-4' }, [
    h('div', { class: 'flex items-center gap-2' }, [
      h('div', { class: 'grid h-7 w-7 place-items-center rounded-lg text-[9px] font-bold text-white', style: { background: 'var(--accent)' } }, 'L'),
      h('span', { class: 'text-[10px] font-semibold' }, 'Luma Studio')
    ]),
    h('div', { class: 'flex items-center gap-4' }, [
      h('span', { class: 'hidden text-[9px] text-zinc-400 sm:inline' }, 'Benefícios'),
      h('span', { class: 'hidden text-[9px] text-zinc-400 sm:inline' }, 'Cases'),
      h('span', { class: 'rounded-full px-3 py-1.5 text-[9px] font-semibold text-white', style: { background: 'var(--accent)' } }, 'Começar')
    ])
  ]),
  h('div', { class: 'relative overflow-hidden px-5 pb-5 pt-7 text-center sm:px-8' }, [
    h('div', { class: 'pointer-events-none absolute left-1/2 top-0 h-44 w-44 -translate-x-1/2 rounded-full opacity-20 blur-3xl', style: { background: 'var(--accent)' } }),
    h('div', { class: 'relative' }, [
      h('span', { class: 'inline-flex rounded-full px-3 py-1 text-[9px] font-semibold', style: { background: 'var(--accent-soft)', color: 'var(--accent)' } }, 'Nova experiência'),
      h('p', { class: 'mx-auto mt-4 max-w-sm text-2xl font-semibold tracking-[-0.05em] sm:text-3xl' }, 'Uma oferta clara merece uma página impossível de ignorar.'),
      h('p', { class: 'mx-auto mt-3 max-w-md text-[10px] leading-5 text-zinc-500' }, 'Apresente valor, construa confiança e conduza o visitante até a ação principal sem distrações.'),
      h('div', { class: 'mt-4 flex justify-center gap-2' }, [
        h('span', { class: 'rounded-full px-4 py-2 text-[9px] font-semibold text-white', style: { background: 'var(--accent)' } }, 'Quero começar'),
        h('span', { class: 'rounded-full bg-zinc-100 px-4 py-2 text-[9px] font-semibold text-zinc-600' }, 'Ver como funciona')
      ])
    ])
  ]),
  h('div', { class: 'grid grid-cols-3 gap-2 px-5 pb-5 sm:px-8' }, [
    ['+clareza', 'na proposta'], ['mobile', 'first'], ['1 objetivo', 'por página']
  ].map(([a, b]) => h('div', { class: 'rounded-xl bg-zinc-50 p-3 text-center ring-1 ring-zinc-100' }, [
    h('p', { class: 'text-xs font-semibold' }, a),
    h('p', { class: 'mt-1 text-[8px] text-zinc-400' }, b)
  ])))
]))

const IntegrationsPreview = defineComponent(() => () => h('div', { class: 'grid h-full gap-4' }, [
  h('div', { class: 'grid grid-cols-4 gap-2' }, [card('Conectadas', '4'), card('Eventos', '18,4k'), card('Sucesso', '99,7%'), card('Latência', '184ms')]),
  h('div', { class: 'grid flex-1 grid-cols-3 gap-3' }, ['WhatsApp', 'Open Finance', 'Pagamentos', 'CRM', 'ERP', 'E-mail'].map((t, i) => h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('div', { class: 'flex items-center justify-between' }, [h('div', { class: 'grid h-9 w-9 place-items-center rounded-xl bg-white/10 text-[9px] font-bold' }, t.slice(0, 2).toUpperCase()), h('span', { class: `h-2.5 w-2.5 rounded-full ${i < 4 ? 'bg-emerald-400' : 'bg-zinc-600'}` })]), h('p', { class: 'mt-4 text-[10px] font-semibold' }, t), h('p', { class: 'mt-2 text-[9px] text-zinc-500' }, i < 4 ? 'Conectado' : 'Disponível')]))),
  h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 font-mono text-[9px] text-zinc-400 ring-1 ring-white/10' }, ['12:51:08  200  webhook processado', '12:49:42  200  sincronização concluída', '12:46:10  202  evento enfileirado'].map(t => h('p', { class: 'mt-2 first:mt-0' }, t)))
]))

const ExecutivePreview = defineComponent(() => () => h('div', { class: 'grid h-full gap-4' }, [
  h('div', { class: 'grid grid-cols-4 gap-2' }, [card('Receita', 'R$ 128k'), card('Margem', '31,8%'), card('NPS', '74'), card('Conversão', '18,6%')]),
  h('div', { class: 'grid flex-1 grid-cols-[1.3fr_.7fr] gap-3' }, [
    h('div', { class: 'flex items-end gap-2 rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [46, 58, 52, 69, 73, 68, 82, 78, 91, 96].map((v, i) => h('span', { class: 'flex-1 rounded-t-md', style: { height: `${v}%`, background: i === 9 ? 'var(--accent)' : 'rgba(255,255,255,.65)' } }))),
    h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('p', { class: 'text-xs font-semibold text-zinc-400' }, 'Metas'), ...[[84, 'Receita'], [72, 'Eficiência'], [91, 'Retenção']].map(([v, l]) => h('div', { class: 'mt-5' }, [h('div', { class: 'flex justify-between text-[10px] text-zinc-400' }, [h('span', l), h('span', `${v}%`)]), h('div', { class: 'mt-2 h-2 rounded-full bg-white/10' }, [h('div', { class: 'h-2 rounded-full', style: { width: `${v}%`, background: 'var(--accent)' } })])]))])
  ])
]))

const AutomationPreview = defineComponent(() => () => h('div', { class: 'grid h-full grid-cols-[1fr_.5fr] gap-3' }, [
  h('div', { class: 'space-y-3' }, ['Lembrete de cobrança', 'Novo lead comercial', 'Sincronização financeira'].map((t, i) => h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('div', { class: 'flex items-center justify-between' }, [h('p', { class: 'text-xs font-semibold' }, t), h('span', { class: 'rounded-full bg-emerald-400/10 px-2 py-1 text-[9px] font-semibold text-emerald-300' }, 'Ativa')]), h('div', { class: 'mt-4 flex items-center gap-2' }, ['Gatilho', 'Regra', 'Ação'].map((s, j) => [h('span', { class: 'rounded-full bg-white/[0.07] px-2 py-1 text-[9px] text-zinc-400' }, s), j < 2 ? h('span', { class: 'text-zinc-600' }, '→') : null]))]))),
  h('div', { class: 'space-y-3' }, [card('Execuções', '385'), card('Sucesso', '99,2%'), h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('p', { class: 'text-xs font-semibold text-zinc-400' }, 'Últimos eventos'), ...['Cobrança enviada', 'Lead distribuído', 'Sync concluído'].map(t => h('p', { class: 'mt-3 text-[9px] text-zinc-500' }, t))])])
]))

const SaasPreview = defineComponent(() => () => h('div', { class: 'grid h-full gap-4' }, [
  h('div', { class: 'flex items-center justify-between rounded-2xl p-4 ring-1 ring-white/10', style: { background: 'linear-gradient(135deg,var(--accent),#111827)' } }, [h('div', [h('p', { class: 'text-[10px] text-white/60' }, 'Workspace'), h('p', { class: 'mt-1 text-lg font-semibold' }, 'Acme Studio')]), h('span', { class: 'rounded-full bg-white/10 px-3 py-1 text-[9px] font-semibold' }, 'Plano Pro')]),
  h('div', { class: 'grid grid-cols-4 gap-2' }, [card('Projetos', '3'), card('Membros', '8'), card('Eventos', '12,8k'), card('Uptime', '99,99%')]),
  h('div', { class: 'grid flex-1 grid-cols-[1fr_.5fr] gap-3' }, [
    h('div', { class: 'grid grid-cols-2 gap-3' }, ['Customer Experience', 'Operações', 'Financeiro', 'Analytics'].map((t, i) => h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('div', { class: 'h-9 w-9 rounded-xl bg-white/10' }), h('p', { class: 'mt-4 text-[10px] font-semibold' }, t), h('p', { class: 'mt-2 text-[9px] text-zinc-500' }, i === 3 ? 'Em planejamento' : 'Ativo')]))),
    h('div', { class: 'rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' }, [h('p', { class: 'text-xs font-semibold text-zinc-400' }, 'Equipe'), ...['Gabrielle', 'Lucas', 'Marina'].map((t, i) => h('div', { class: 'mt-4 flex items-center gap-3' }, [h('div', { class: 'grid h-8 w-8 place-items-center rounded-lg bg-white/10 text-[9px] font-bold' }, t[0]), h('div', [h('p', { class: 'text-[10px] font-semibold' }, t), h('p', { class: 'text-[9px] text-zinc-500' }, i === 0 ? 'Owner' : 'Member')])]))])
  ])
]))

const previewMap = {
  finance: FinancePreview,
  crm: CrmPreview,
  inbox: InboxPreview,
  ai: AiPreview,
  backoffice: BackofficePreview,
  calendar: CalendarPreview,
  portal: PortalPreview,
  landing: LandingPreview,
  integrations: IntegrationsPreview,
  executive: ExecutivePreview,
  automation: AutomationPreview,
  saas: SaasPreview
}

const previewComponent = computed(() => previewMap[solution.value.preview] || FinancePreview)

function handlePointerMove(event) {
  const width = window.innerWidth || 1
  const height = window.innerHeight || 1
  pointer.x = Math.max(0, Math.min(100, (event.clientX / width) * 100))
  pointer.y = Math.max(0, Math.min(100, (event.clientY / height) * 100))
}

function handleScroll() {
  showScrollTop.value = window.scrollY > 520
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function setupReveal() {
  const elements = document.querySelectorAll('[data-reveal]')
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach(element => element.classList.add('is-visible'))
    return
  }

  observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible')
        observer?.unobserve(entry.target)
      }
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -45px 0px' })

  elements.forEach(element => observer.observe(element))
}

onMounted(() => {
  setupReveal()
  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onBeforeUnmount(() => {
  observer?.disconnect()
  window.removeEventListener('scroll', handleScroll)
})

</script>

<style scoped>
:global(:root) {
  --page: #f5f5f7;
  --surface: rgba(255, 255, 255, .90);
  --card: rgba(255, 255, 255, .76);
  --chip: rgba(245, 245, 247, .92);
  --glass: rgba(255, 255, 255, .54);
  --glass-strong: rgba(255, 255, 255, .84);
  --text: #27272a;
  --text-strong: #09090b;
  --muted: #66666d;
  --muted-2: #a1a1aa;
  --inverse: #fff;
  --border: rgba(24, 24, 27, .085);
  --focus: rgba(113, 113, 122, .18);
  --shadow-soft: 0 12px 38px rgba(24, 24, 27, .07);
  --shadow-large: 0 32px 100px rgba(24, 24, 27, .105);
  --orb-one: rgba(147, 161, 185, .22);
  --orb-two: rgba(203, 213, 225, .31);
  --cta: rgba(255, 255, 255, .92)
}

:global(html[data-theme='dark']) {
  --page: #08090b;
  --surface: rgba(15, 16, 19, .90);
  --card: rgba(19, 20, 24, .76);
  --chip: rgba(26, 27, 31, .92);
  --glass: rgba(19, 20, 23, .54);
  --glass-strong: rgba(18, 19, 22, .84);
  --text: #e4e4e7;
  --text-strong: #fafafa;
  --muted: #a1a1aa;
  --muted-2: #5f6169;
  --inverse: #09090b;
  --border: rgba(255, 255, 255, .085);
  --focus: rgba(212, 212, 216, .13);
  --shadow-soft: 0 12px 42px rgba(0, 0, 0, .30);
  --shadow-large: 0 36px 120px rgba(0, 0, 0, .50);
  --orb-one: rgba(71, 85, 105, .23);
  --orb-two: rgba(63, 63, 70, .28);
  --cta: rgba(18, 19, 22, .94)
}

:global(html) {
  background: var(--page);
  scroll-behavior: smooth
}

.site-page {
  transition: background-color 350ms ease, color 350ms ease
}

.ambient-grid {
  opacity: .31;
  background-image: linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: linear-gradient(to bottom, black, transparent 84%)
}

.ambient-noise {
  opacity: .021;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.85'/%3E%3C/svg%3E")
}

.pointer-glow {
  background: radial-gradient(42rem circle at var(--pointer-x) var(--pointer-y), var(--orb-one), transparent 68%)
}

.ambient-orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(95px)
}

.ambient-orb-one {
  left: -11rem;
  top: 8rem;
  width: 34rem;
  height: 34rem;
  background: var(--orb-one);
  animation: float-a 14s ease-in-out infinite
}

.ambient-orb-two {
  right: -12rem;
  top: 48rem;
  width: 38rem;
  height: 38rem;
  background: var(--orb-two);
  animation: float-b 18s ease-in-out infinite
}

.primary-button {
  color: var(--inverse);
  background: var(--text-strong);
  box-shadow: 0 8px 24px rgba(0, 0, 0, .1), inset 0 1px 0 rgba(255, 255, 255, .12);
  transition: transform 300ms ease, box-shadow 300ms ease
}

.primary-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 32px rgba(0, 0, 0, .16)
}

.secondary-button {
  color: var(--text-strong);
  background: var(--glass-strong);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-soft);
  backdrop-filter: blur(18px);
  transition: transform 300ms ease, border-color 300ms ease
}

.secondary-button:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--text-strong) 18%, transparent)
}

.accent-pill,
.accent-icon {
  background: var(--accent-soft);
  color: var(--accent);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--accent) 10%, transparent)
}

.accent-label {
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .18em;
  color: var(--accent)
}

.accent-halo,
.accent-process-glow {
  background: var(--accent-soft)
}

.accent-dark-glow {
  background: var(--accent);
  opacity: .20
}

.preview-frame {
  background: linear-gradient(135deg, color-mix(in srgb, var(--text-strong) 16%, transparent), color-mix(in srgb, var(--text-strong) 4%, transparent));
  border: 1px solid var(--border);
  box-shadow: var(--shadow-large), inset 0 1px 0 rgba(255, 255, 255, .10)
}

.metric-card,
.pain-card,
.module-card,
.process-card {
  transition: transform 450ms ease, border-color 450ms ease, box-shadow 450ms ease
}

.metric-card:hover,
.pain-card:hover,
.module-card:hover,
.process-card:hover {
  transform: translateY(-5px);
  border-color: color-mix(in srgb, var(--text-strong) 14%, transparent);
  box-shadow: var(--shadow-soft)
}

.section-title {
  max-width: 54rem;
  font-size: clamp(2.35rem, 4.7vw, 4rem);
  line-height: 1.02;
  font-weight: 600;
  letter-spacing: -.055em;
  color: var(--text-strong)
}

.flow-line {
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  background: linear-gradient(to bottom, transparent, var(--border) 24%, var(--border) 76%, transparent)
}

.flow-node {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 9px;
  height: 9px;
  border: 2px solid var(--page);
  border-radius: 9999px;
  background: var(--text-strong);
  box-shadow: 0 0 0 5px color-mix(in srgb, var(--text-strong) 8%, transparent);
  transform: translate(-50%, -50%)
}

.curve-transition {
  position: relative;
  height: 5.5rem;
  overflow: hidden
}

.curve-transition::before {
  content: '';
  position: absolute;
  left: 12%;
  top: 50%;
  width: 76%;
  height: 5rem;
  border-top: 1px solid var(--border);
  border-radius: 50%;
  transform: translateY(18%)
}

.curve-dot {
  position: absolute;
  left: 50%;
  top: 2.25rem;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: var(--text-strong);
  transform: translateX(-50%)
}

.process-shell,
.faq-panel {
  backdrop-filter: blur(24px)
}

.story-panel,
.dark-panel {
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .08)
}

.dark-panel {
  background: radial-gradient(circle at 85% 20%, rgba(255, 255, 255, .07), transparent 30%), #090a0c
}

.dark-panel-grid {
  opacity: .31;
  background-image: linear-gradient(to right, rgba(255, 255, 255, .055) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, .055) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(to left, black, transparent 74%)
}

.process-line {
  background: linear-gradient(to right, transparent, var(--border) 12%, var(--border) 88%, transparent)
}

.cta-panel {
  background: linear-gradient(135deg, var(--cta), color-mix(in srgb, var(--cta) 85%, var(--page)))
}

[data-reveal] {
  opacity: 0;
  transform: translateY(26px) scale(.992);
  transition: opacity 780ms cubic-bezier(.22, .82, .22, 1), transform 780ms cubic-bezier(.22, .82, .22, 1)
}

[data-reveal].is-visible {
  opacity: 1;
  transform: translateY(0) scale(1)
}

.scroll-top-enter-active,
.scroll-top-leave-active {
  transition: opacity .25s ease, transform .25s ease
}

.scroll-top-enter-from,
.scroll-top-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(.92)
}

@keyframes float-a {

  0%,
  100% {
    transform: translate3d(0, 0, 0)
  }

  50% {
    transform: translate3d(3rem, 2rem, 0)
  }
}

@keyframes float-b {

  0%,
  100% {
    transform: translate3d(0, 0, 0)
  }

  50% {
    transform: translate3d(-3rem, -2rem, 0)
  }
}

@media(max-width:640px) {
  .ambient-grid {
    background-size: 44px 44px
  }

  .curve-transition::before {
    left: 2%;
    width: 96%
  }
}

@media(prefers-reduced-motion:reduce) {

  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important
  }

  [data-reveal] {
    opacity: 1;
    transform: none
  }
}
</style>